import { BI_CONFIGURATION_FIELD } from "@wetalk/domain";
import { BiConfigurationEditor } from "./bi-configuration.js";
import { apiErrorText } from "../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent setup"` — the wizard's form, drawn to the Claude Design export of 2026-10-09.
 *
 * WHAT CHANGED WITH THE 2026-10-09 EXPORT (design sync, unit B):
 *
 *   · THE STEP LIST is a row of numbered marks joined by a line; a step done shows a tick and goes back to it, the
 *     current one is ringed, the rest wait. A template with an order book splits its content into Menu, Hours and
 *     Orders (`?step=` 2 to 7); any other template keeps one Content step (2 to 5). Decided by the field kinds
 *     (`wizardStepsOf`), never by the template's code.
 *   · ONE COLUMN, 640 wide: the step's title and standfirst above its controls, Back / Continue below a rule, and the
 *     reason beside a disabled Continue ("Attach the menu file to continue.").
 *   · ORDERS: where orders go is two cards — the WeTalk order book, and the shop's own system (not built yet, so it is
 *     shown and cannot be chosen) — and how customers pay is three: a checkout link by SMS (only while the account's
 *     online payment is on), cash or card on delivery (the default) or no payment.
 *   · LANGUAGES: chosen languages are chips with an "Add language" list; the greeting language is chosen apart; the
 *     greeting box starts filled with the suggestion made from the business's own name; each language has its own
 *     voice row with Play.
 *   · CHANNELS: calls are always on, web chat is one box; capacity is the prototype's five choices with those above the
 *     organization's limit disabled, and "Request more lines" opens the request. The number is connected after the
 *     build (the Review step says so), so this step no longer asks it. "Record calls" stays a choice here (owner
 *     decision 2026-10-09), on by default, sent as `recording_enabled` and shown on the Review step.
 *   · REVIEW: warnings first, the agent's name, a card per part with Edit, and the price with a typical cost.
 *
 * AMENDED 2026-10-09 (plan-015 AGC-UI, gaps G3, G4, G8, G12):
 *
 *   · HOURS: the week card's time zone is the VoiceAgent's own (the workspace's until changed) and changes; "Special
 *     dates" is drawn under the week; and under the card, "Out of hours" and "When every line is busy" are the agent
 *     page's two cards — say you are closed / take a message / forward, queue (with the longest wait, two minutes to
 *     start) / take a message / forward. "Forward to a phone" is greyed with its reason while forwarding is closed
 *     (no route tells the wizard it is open yet). All of it is sent as `answering` (IC-DG-2).
 *   · CHANNELS: the busy sentence names the wait the build will use, from the choice above — never a fixed two minutes.
 *   · ORDERS: "Send a checkout link by SMS" sends `online` alone (owner answer 2).
 *   · REVIEW: the Opening hours card lists the special dates, the time zone and the two choices.
 *
 * AMENDED 2026-10-13 (plan-018 WAPP-A, design §30.12): the content step draws the field set's new facts by kind — a
 * `website` field is an address box with its sentence per refusal reason, an upload that takes several files lists them
 * (each removable) under a dropzone that takes up to its limit, and a required group's refusal names every alternative
 * under the group's last field. The api's information refusals come back in the portal's language and under their
 * control (`./information-input.ts`), and the Review step offers the way back to it.
 *
 * AMENDED 2026-10-14 (owner decision; ADR 0061 amendment, design §30.19): for an agent_template whose engine answers
 * from the owner's information (`answersAtAllHoursOf`, the catalogue's engine capability), the Hours step says plainly
 * that opening hours decide when callers can be put through to a person and that the agent answers questions and takes
 * messages at any time; the "Out of hours" choice and its Review row are not drawn (the runtime never plays them for
 * it), and a carried out-of-hours "forward" is sent as "take a message" (`allHoursChoiceOf`). Delivery is unchanged.
 *
 * Everything the screen decides that is not drawing is in `./wizard-rules.ts`. The answers persist across a reload
 * (`./wizard-draft.ts`) and the documents go straight to storage on Build (`./document-upload.ts`), as before.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Button, Field, Icon, Input, Tag, Textarea } from "@wetalk/ui";
import type { AgentTemplateField } from "@wetalk/domain";
import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import { HelpFillScope } from "../../../help/fill/help-fill-scope.js";
import { useHelpFillable } from "../../../help/fill/use-help-fillable.js";
import { useHelpPlace } from "../../../help/place/use-help-place.js";
import { QUERY_KEY } from "../../../api/query.js";
import type { AgentTemplateCardView, LanguagePackView, NumberAvailabilityView, VoiceView } from "../../../api/gateway.js";
import type { SharedVoiceAgentConfigurationView } from "./shared-configuration-gateway.js";
import { rememberBuildOrigin } from "../../building/build-origin.js";
import { rememberReadyBuild } from "../../../shell/ready-banner.js";
import { LanguageBetaMark, LanguageBetaTag, betaLanguageLabelOf, languageBetaNoteOf, voiceGenderTextOf } from "../../../shell/language-beta.js";
import { QuotaRequestDialog } from "../../billing/quota-request-dialog.js";
import {
  MENU_PREVIEW_FIELD_CODE,
  MENU_PREVIEW_REFERENCE_SCHEMA,
  menuPreviewFootText,
  menuPreviewHeadText,
  menuSamplePriceText,
  progressText,
  uploadDocumentsThenStart,
  watchMenuPreview,
  type AttachedDocument,
  type DocumentUploadProgress,
  type MenuPreviewState,
} from "./document-upload.js";
import { descriptionOf, fieldFormatOf, placeholderOf } from "./field-format.js";
import { contentTypeOfFile } from "../../../api/slice/agent-document.js";
import {
  dropFilesText,
  fileLimitHintText,
  fileLimitReachedText,
  fileUnreadableText,
  filesLeftOutText,
  informationRefusalOf,
  removeFileText,
  removeText,
  requiredGroupKeyOf,
  typedUploadCopyOf,
  websiteAddressSentOf,
  websiteDescriptionText,
  websitePlaceholderText,
  wizardErrorText,
  type InformationRefusal,
} from "./information-input.js";
import { SetupCost } from "./setup-cost.js";
import { calendarDateIn } from "@wetalk/domain";

import {
  DEFAULT_WAIT_MINUTE,
  afterHoursOptionOf,
  allHoursChoiceOf,
  answersAtAllHoursOf,
  busyLineSentenceOf,
  busyOptionOf,
  closedWeek,
  MAX_SHIFT_PER_DAY,
  forwardClosedReasonOf,
  forwardLimitSentenceOf,
  forwards,
  openingHoursLine,
  specialDateLine,
  timeZoneLabelOf,
  waitNoteOf,
  type AnsweringChoice,
  type ForwardFact,
  type OpeningWeek,
  type SpecialDateEntry,
} from "./opening-hours.js";
import { AnsweringChoiceGroup, ForwardNumberField, LongestWaitField, OpeningWeekEditor } from "./opening-hours-week.js";
import { openingHoursFillOf, openingHoursFillProblemOf, specialDateOfFill, weekOfFill } from "./opening-hours-fill.js";
import {
  clearDraft,
  clearDraftFile,
  freshAnswer,
  loadDraft,
  loadDraftFile,
  saveDraft,
  saveDraftFile,
  type CreatedInDraft,
  type PaymentMethodCode,
  type WizardAnswer,
} from "./wizard-draft.js";
import {
  DEFAULT_PAYMENT_METHOD,
  PAYMENT_CHOICE_METHOD,
  PROBLEM_KEY,
  answeringOf,
  asksOpeningHours,
  buildRequestOf,
  channelUnitChoiceOf,
  contentFieldOf,
  contentFieldOfPart,
  attachedNameOf,
  duplicateNameWarning,
  fileLimitOf,
  fileSlotOf,
  fileSlotsOf,
  isSlotAttached,
  namedFileOf,
  takesSeveralFiles,
  firstIncompleteWizardStep,
  isAttached,
  languageNameOf,
  orderBookFieldOf,
  paymentChoiceOf,
  resolve,
  speaksOneLanguage,
  summaryTermOf,
  uploadFileOf,
  wizardNextHintOf,
  wizardStepProblemOf,
  wizardHelpPlaceOf,
  wizardHelpStateOf,
  wizardStepsOf,
  type ConversationDirection,
  type LanguageVoiceRow,
  type PaymentChoice,
  type Resolved,
  type WizardAnswering,
  type WizardContext,
  type WizardStepDef,
} from "./wizard-rules.js";
import {
  LanguageVoicePlayButton,
  browserSamplePlaybackOf,
  playKeyOf,
  useVoiceSamplePlayer,
  type SamplePlaybackOf,
  type VoiceSamplePlayer,
} from "./voice-sample-play.js";
import "../screen.css";
import "./new-agent.css";
import "./menu-preview.css";

export interface WizardFormProps {
  readonly card: AgentTemplateCardView;
  readonly step: number;
  readonly onStep: (step: number, replace?: boolean) => void;
  readonly draftKey: string;
  readonly languagePack: readonly LanguagePackView[];
  readonly voice: readonly VoiceView[];
  readonly ceiling: number;
  readonly numberAvailability: NumberAvailabilityView;
  readonly accountName: string;
  readonly voiceAgentName: readonly { readonly voiceAgentId: string; readonly name: string }[];
  readonly suggestedLanguage: string | null;
  /** plan-014 GAP: the shared configuration this agent starts from, or `null`. */
  readonly shared: SharedVoiceAgentConfigurationView | null;
  /** The workspace's time zone, which the build reads the opening hours in; `null` while unknown. */
  readonly timeZone: string | null;
  /** Whether a caller would be offered online payment now (`GET /v1/online-payment/status`). */
  readonly onlinePaymentOffered: boolean;
  /** Whether web chat can be switched on (the messaging catalogue says `open`). */
  readonly webChatConnectable: boolean;
  /** How a fetched voice sample is played. A suite hands in its own; the browser's is the default. */
  readonly playbackOf?: SamplePlaybackOf;
  /**
   * plan-015: whether forwarding to a phone can be chosen, with its ring and time limit, when a route tells the wizard.
   * Absent or `null` (no route serves it before a VoiceAgent exists) reads as closed: the choice stays greyed.
   */
  readonly forwardFact?: ForwardFact | null;
}

/*
 * plan-015 (G1U's note, AGC-UI): a shared configuration's per-language voices and answering answers. Merged here,
 * optional, because `shared-configuration-gateway.ts` (no owner in plan-015) reads only `voice_id` today; once it reads
 * `voice_by_language`, `time_zone`, `special_date` and the choices from `SharedVoiceAgentConfiguration`, the wizard
 * starts from them — and until then from the share's single voice and the wizard's own defaults.
 */
declare module "./shared-configuration-gateway.js" {
  interface SharedVoiceAgentConfigurationView {
    readonly voiceByLanguage?: Readonly<Record<string, string>>;
    /** The pinned version's own zone; `null` when it carries none. */
    readonly timeZone?: string | null;
    readonly specialDate?: readonly SpecialDateEntry[];
    /** `null` when the pinned version's behaviours are not expressible as the choices. */
    readonly answeringChoice?: AnsweringChoice | null;
  }
}

/** The first answers of a wizard that starts from a shared configuration: its languages, voices, greeting and the rest. */
export function answerFromShared(shared: SharedVoiceAgentConfigurationView): WizardAnswer {
  return {
    ...freshAnswer(),
    languageCode: [...shared.languageCode],
    voiceId: shared.voiceId,
    ...(shared.voiceByLanguage === undefined ? {} : { voiceByLanguage: { ...shared.voiceByLanguage } }),
    greeting: shared.greeting,
    greetingEdited: true,
    channelUnitCount: shared.channelUnitCount,
    recordingEnabled: shared.recordingEnabled,
    ...(shared.timeZone === undefined || shared.timeZone === null ? {} : { timeZone: shared.timeZone }),
    ...(shared.specialDate === undefined ? {} : { specialDate: [...shared.specialDate] }),
    ...(shared.answeringChoice === undefined || shared.answeringChoice === null
      ? {}
      : { answeringChoice: shared.answeringChoice }),
  };
}

// ── the copy that depends on the step ─────────────────────────────────────────────────────

function headOf(def: WizardStepDef, card: AgentTemplateCardView): { readonly title: string; readonly dek: string } {
  if (def.kind === "content") {
    if (def.part === "menu") return { title: portalText("operations", "your_menu_b421d070"), dek: portalText("operations", "the_agent_reads_items_sizes_prices_and_extras_from_it_you_2ea80e98") };
    if (def.part === "hours") {
      return {
        title: portalText("operations", "when_are_you_open_a70bf4d6"),
        dek: portalText("operations", "merge.wizard.the_agent_takes_orders_during_these_hours_outside_them_it_tells_ca.432598"),

      };
    }
    if (def.part === "orders") return { title: portalText("operations", "where_do_orders_go_773a018e"), dek: portalText("operations", "merge.wizard.choose_where_the_agent_sends_each_order.d91b21") };
    if (card.fieldSet.some((field) => field.code === BI_CONFIGURATION_FIELD)) return { title: card.contentTitle, dek: portalText("operations", "bi_setup_intro") };
    return { title: card.contentTitle, dek: card.contentDek };
  }
  if (def.kind === "language") return { title: portalText("operations", "languages_and_voice_7c8b55f5"), dek: portalText("operations", "choose_the_languages_your_agent_speaks_and_a_voice_for_eac_1edc3c36") };
  if (def.kind === "medium") return { title: portalText("operations", "merge.wizard.where_customers_reach_the_agent.b93f42"), dek: portalText("operations", "merge.wizard.and_how_many_it_can_talk_to_at_the_same_time.c5fb87") };
  return {
    title: portalText("operations", "review_and_build_57f88962"),
    dek: portalText("operations", "merge.wizard.building_can_take_a_little_while_you_can_leave_this_page_the_agent.85ed66"),
  };
}

/** The export of 2026-10-10 words each choice the same on the Orders step and on Review (its `payOpts` and `PAY`). */
const PAYMENT_OPTION: readonly { readonly choice: PaymentChoice; readonly label: string }[] = [
  { choice: "link", get label() { return portalText("operations", "send_a_checkout_link_by_sms_e5aa0f9b"); } },
  { choice: "delivery", get label() { return portalText("operations", "merge.wizard.pay_when_the_order_arrives_or_is_picked_up.0ab5a2"); } },
  { choice: "none", get label() { return portalText("operations", "merge.wizard.don_t_ask_about_payment.9a77f5"); } },

];

/** The Review step's payment line; a draft or a shared answer the three choices do not name says what it is. */
function paymentSummaryLineOf(method: readonly PaymentMethodCode[]): string {
  const choice = paymentChoiceOf(method);
  const found = PAYMENT_OPTION.find((one) => one.choice === choice);
  if (found !== undefined) return found.label;
  return method.includes("cash_on_delivery") ? portalText("operations", "cash_on_delivery_eda24c69") : portalText("operations", "card_on_delivery_78c3f220");

}

// ── the form ─────────────────────────────────────────────────────────────────────────────────

export function WizardForm(props: WizardFormProps): ReactNode {
  usePortalTranslation("operations");
  const { card, step, onStep, draftKey } = props;
  const gateway = useGateway();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const player = useVoiceSamplePlayer(props.playbackOf ?? browserSamplePlaybackOf);

  // #15: the draft this tab kept, read once, before the first paint.
  const [stored] = useState(() => loadDraft(draftKey));
  const [answer, setAnswer] = useState<WizardAnswer>(stored?.answer ?? (props.shared === null ? freshAnswer() : answerFromShared(props.shared)));
  const [created, setCreated] = useState<CreatedInDraft | null>(stored?.created ?? null);
  const [file, setFile] = useState<Readonly<Record<string, File>>>({});
  const [biIssue, setBiIssue] = useState<string | null>(null);
  const [filesRead, setFilesRead] = useState(false);
  const [upload, setUpload] = useState<Readonly<Record<string, DocumentUploadProgress>>>(() =>
    Object.fromEntries((stored?.created?.confirmedField ?? []).map((code) => [code, { phase: "confirmed", fraction: 1, message: null }] as const)),
  );
  const [attempted, setAttempted] = useState<ReadonlySet<number>>(new Set());
  const [restoredNoticeShown, setRestoredNoticeShown] = useState(stored !== null);

  // plan-018 WAPP-A: a file is kept under its slot — the field code, or `<code>/<name>` for an upload taking several.
  const storedSlot = useMemo(() => fileSlotsOf(card.fieldSet, stored?.answer.fieldValue ?? {}).map((one) => one.slot), [card, stored]);
  // The api's information refusal, placed under its control until that answer changes.
  const [refusal, setRefusal] = useState<InformationRefusal | null>(null);

  // The files the draft names, back from IndexedDB (where the browser allows it).
  useEffect(() => {
    let mounted = true;
    void loadDraftFile(draftKey, storedSlot).then((found) => {
      if (!mounted) return;
      setFile((current) => ({ ...found, ...current }));
      setFilesRead(true);
    });
    return () => {
      mounted = false;
    };
  }, [draftKey, storedSlot]);

  const confirmedField = useMemo(
    () =>
      Object.entries(upload)
        .filter(([, progress]) => progress.phase === "confirmed")
        .map(([code]) => code),
    [upload],
  );

  // Every answer, as it changes.
  useEffect(() => {
    saveDraft(draftKey, { answer, created: created === null ? null : { ...created, confirmedField } });
  }, [draftKey, answer, created, confirmedField]);

  // The prototype's default payment — cash or card on delivery — until the person chooses another.
  const effective: WizardAnswer = { ...answer, paymentMethod: answer.paymentMethod ?? DEFAULT_PAYMENT_METHOD };
  const context: WizardContext = {
    card,
    answer: effective,
    file,
    created: created === null ? null : { ...created, confirmedField },
    languagePack: props.languagePack,
    voice: props.voice,
    suggestedLanguage: props.suggestedLanguage,
    ceiling: props.ceiling,
    newNumberAvailable: props.numberAvailability.available,
    accountName: props.accountName,
    otherName: props.voiceAgentName.filter((one) => one.voiceAgentId !== created?.voiceAgentId).map((one) => one.name),
    forwardFact: props.forwardFact ?? null,
  };
  // plan-015 AGC-UI: the VoiceAgent's own zone, its special dates and the out-of-hours and busy choices.
  // 2026-10-14: an engine that answers at every hour never plays the out-of-hours choice (`allHoursChoiceOf`).
  const answersAtAllHours = answersAtAllHoursOf(card.code);
  const readAnswering = answeringOf(effective, props.timeZone);
  const answering = answersAtAllHours ? { ...readAnswering, choice: allHoursChoiceOf(readAnswering.choice) } : readAnswering;
  // plan-015 G1U: every chosen language has its own voice (`resolved.voiceRow`), sent as `voice_by_language`.
  const resolved = resolve(context);
  const voiceRow = resolved.voiceRow;

  // plan-015 PRV (G6, owner answer 10): the menu is read as soon as it is attached, and the card shows what was
  // understood. Redone only when the FILE changes — the language is the one in force when it was attached (a language
  // changed later is the build's to read in). The preview answered belongs to the file it was asked for.
  const [menuPreview, setMenuPreview] = useState<{ readonly file: File; readonly state: MenuPreviewState } | null>(null);
  const previewLanguage = useRef<string | undefined>(undefined);
  const firstLanguage = resolved.languageCode[0];
  useEffect(() => {
    previewLanguage.current = firstLanguage;
  }, [firstLanguage]);
  const menuFile = card.referenceSchema === MENU_PREVIEW_REFERENCE_SCHEMA ? file[MENU_PREVIEW_FIELD_CODE] : undefined;
  useEffect(() => {
    const languageCode = previewLanguage.current;
    if (menuFile === undefined || languageCode === undefined) return undefined;
    let stopped = false;
    void watchMenuPreview(
      gateway,
      { agentTemplateCode: card.code, languageCode, file: menuFile },
      (state) => {
        if (!stopped) setMenuPreview({ file: menuFile, state });
      },
      () => stopped,
    );
    return () => {
      stopped = true;
    };
  }, [gateway, card.code, menuFile]);
  const shownPreview = menuPreview !== null && menuPreview.file === menuFile ? menuPreview.state : null;

  const steps = wizardStepsOf(card);
  const lastStep = steps.at(-1)?.step ?? step;
  const def = steps.find((one) => one.step === Math.min(step, lastStep)) ?? (steps[0] as WizardStepDef);
  const firstIncomplete = firstIncompleteWizardStep(steps, context, resolved);
  const webChatOn = answer.medium.includes("web_chat") && props.webChatConnectable;

  // #16: a URL for a step ahead of an unfinished one lands on the unfinished one, showing why.
  const sentBack = filesRead && step > firstIncomplete;
  if (sentBack && !attempted.has(firstIncomplete)) {
    setAttempted(new Set([...attempted, firstIncomplete]));
  }
  useEffect(() => {
    if (sentBack) onStep(firstIncomplete, true);
  }, [sentBack, firstIncomplete, onStep]);

  const build = useMutation({
    mutationFn: async (): Promise<CreatedInDraft> => {
      const built = {
        ...buildRequestOf(context, resolved, answering),
        // The number is connected after the build (the Review step's "Phone" row); calls always, web chat when chosen.
        numberChoice: "bring_my_own" as const,
        medium: webChatOn ? ["voice", "web_chat"] : ["voice"],
      };
      const request = props.shared === null ? built : { ...built, resourceShareId: props.shared.resourceShareId };
      let made = created;
      if (made === null) {
        const answered = await gateway.createVoiceAgent(request);
        made = { voiceAgentId: answered.voiceAgentId, buildJobId: answered.buildJobId, confirmedField: [] };
        setCreated(made);
      }
      // One document per file slot (a field code, or `<code>/<name>` for an upload that takes several).
      const attached = fileSlotsOf(contentFieldOf(card, answer.fieldValue), answer.fieldValue).flatMap((one): AttachedDocument[] => {
        const picked = file[one.slot];
        return picked === undefined ? [] : [{ fieldCode: one.slot, file: uploadFileOf(one.field, picked) }];
      });
      await uploadDocumentsThenStart(
        gateway,
        made.voiceAgentId,
        attached,
        new Set(confirmedField),
        (fieldCode, progress) => {
          setUpload((current) => ({ ...current, [fieldCode]: progress }));
        },
        (raised) => wizardErrorText(raised, card.fieldSet, answer.fieldValue),
      );
      return made;
    },
    onError: (raised) => {
      setRefusal(informationRefusalOf(raised, card.fieldSet, answer.fieldValue));
    },
    onSuccess: async (made) => {
      // The App shell's "is ready to test" banner (the export of 2026-10-10): what this build was given, in words.
      rememberReadyBuild({
        buildJobId: made.buildJobId,
        voiceAgentId: made.voiceAgentId,
        documentName: fileSlotsOf(contentFieldOf(card, answer.fieldValue), answer.fieldValue).map((one) => one.name),
        languageName: voiceRow.map((one) => one.languageName),
        languageCode: voiceRow.map((one) => one.languageCode),
      });
      clearDraft(draftKey);
      void clearDraftFile(draftKey, fileSlotsOf(card.fieldSet, answer.fieldValue).map((one) => one.slot));
      rememberBuildOrigin(queryClient, made.buildJobId, made.voiceAgentId);
      // The shell's agent count and the Agents list read the same key; the new row is theirs.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await navigate({ to: "/building/$buildJobId", params: { buildJobId: made.buildJobId } });
    },
  });

  const change = (next: (current: WizardAnswer) => WizardAnswer): void => {
    setAnswer(next);
    setRefusal(null);
  };

  const problem = wizardStepProblemOf(def, context, resolved);
  const own = attempted.has(def.step) ? problem : {};
  // The api's refusal is said under its control on the content step that asks it (plan-018 WAPP-A).
  const refusalHere =
    refusal !== null &&
    def.kind === "content" &&
    contentFieldOfPart(card, def.part, answer.fieldValue).some((one) => one.code === refusal.key || (one.requiredGroup !== undefined && requiredGroupKeyOf(one.requiredGroup) === refusal.key));
  const shown: Readonly<Record<string, string>> = refusalHere ? { ...own, [refusal.key]: refusal.text } : own;
  const refusalStep = refusal === null ? undefined : steps.find((one) => one.kind === "content" && contentFieldOfPart(card, one.part, answer.fieldValue).some((each) => each.code === refusal.key || (each.requiredGroup !== undefined && requiredGroupKeyOf(each.requiredGroup) === refusal.key)));
  const nextHint = biIssue ?? wizardNextHintOf(def, context, resolved);
  const isSummary = def.kind === "summary";

  const setFieldValue = (code: string, value: string): void => {
    change((current) => ({ ...current, fieldValue: { ...current.fieldValue, [code]: value } }));
  };

  /** A picked file: its name is what the draft records, the file is what is uploaded. */
  const pickFile = (code: string, picked: File): void => {
    change((current) => ({ ...current, fieldValue: { ...current.fieldValue, [code]: picked.name } }));
    setFile((current) => ({ ...current, [code]: picked }));
    void saveDraftFile(draftKey, code, picked);
    // A file picked again after a failure (or a change of mind) is uploaded afresh.
    setUpload((current) => {
      const { [code]: _replaced, ...rest } = current;
      return rest;
    });
  };

  /**
   * plan-018 WAPP-A: files added to an upload that takes several — each under its own slot, the value naming them one
   * per line. A file of a name already there replaces it; what is past the limit is the caller's to refuse.
   */
  const pickFiles = (field: AgentTemplateField, picked: readonly File[]): void => {
    const named = namedFileOf(answer.fieldValue[field.code]);
    const next = [...named, ...picked.map((one) => one.name).filter((name) => !named.includes(name))];
    change((current) => ({ ...current, fieldValue: { ...current.fieldValue, [field.code]: next.join("\n") } }));
    setFile((current) => ({ ...current, ...Object.fromEntries(picked.map((one) => [fileSlotOf(field, one.name), one])) }));
    for (const one of picked) void saveDraftFile(draftKey, fileSlotOf(field, one.name), one);
    setUpload((current) => Object.fromEntries(Object.entries(current).filter(([slot]) => !picked.some((one) => fileSlotOf(field, one.name) === slot))));
  };

  /** plan-018 WAPP-A: one file taken out of an upload that takes several (an uploaded one stays with the agent created). */
  const removeFile = (field: AgentTemplateField, name: string): void => {
    const slot = fileSlotOf(field, name);
    change((current) => ({
      ...current,
      fieldValue: { ...current.fieldValue, [field.code]: namedFileOf(current.fieldValue[field.code]).filter((one) => one !== name).join("\n") },
    }));
    setFile((current) => {
      const { [slot]: _removed, ...rest } = current;
      return rest;
    });
    void clearDraftFile(draftKey, [slot]);
  };

  const goTo = (next: number): void => {
    onStep(next);
  };
  // The help assistant knows the step shown, the steps offered and which fields are empty (owner request 2026-10-17);
  // its "Go to {step}" card moves through this same step change, which lands on the first unfinished step if need be.
  useHelpPlace({
    place: wizardHelpPlaceOf(def),
    placeList: steps.map(wizardHelpPlaceOf),
    state: def.kind === "content" ? wizardHelpStateOf(context, contentFieldOfPart(card, def.part, answer.fieldValue), firstIncomplete > def.step) : [{ code: "step_complete", filled: firstIncomplete > def.step }],
    move: (place) => {
      const target = steps.find((one) => wizardHelpPlaceOf(one) === place);
      if (target !== undefined) goTo(target.step);
    },
  });
  const head = headOf(def, card);
  const orderBook = orderBookFieldOf(card);
  const direction = card.direction;

  return (
    <section className="c-screen">
      <SetupStepper
        agentTemplateName={card.name}
        steps={steps}
        current={def.step}
        onPicker={() => {
          void navigate({ to: "/agents/new" });
        }}
        onStep={goTo}
      />

      <div className="c-setup-body">
        <div className="c-setup-head">
          <h1 className="c-setup-title">{head.title}</h1>
          <p className="c-setup-dek">{head.dek}</p>
          {props.shared === null ? null : (
            <p className="c-note c-setup-restored">
              {portalText("operations", "starting_from_3f3bdce2")}{props.shared.sourceVoiceAgentName}
              {props.shared.sourceAgentVersionLabel === null ? "" : ` (${props.shared.sourceAgentVersionLabel})`}{portalText("operations", "shared_by_10cade2c")}{" "}
              {props.shared.sourceWorkspaceName}{portalText("operations", "its_languages_voice_greeting_and_settings_its_calls_record_30fa33e3")}</p>
          )}
          {restoredNoticeShown ? (
            <p className="c-note c-setup-restored">
              {portalText("operations", "we_kept_your_answers_from_before_the_page_reloaded_dba38f3b")}{" "}
              <button
                type="button"
                className="c-setup-inline-link"
                onClick={() => {
                  clearDraft(draftKey);
                  void clearDraftFile(draftKey, fileSlotsOf(card.fieldSet, answer.fieldValue).map((one) => one.slot));
                  setAnswer(props.shared === null ? freshAnswer() : answerFromShared(props.shared));
                  setFile({});
                  setCreated(null);
                  setUpload({});
                  setAttempted(new Set());
                  setRefusal(null);
                  setRestoredNoticeShown(false);
                  onStep(steps[0]?.step ?? 2);
                }}
              >
                {portalText("operations", "start_over_51dd91dc")}</button>
            </p>
          ) : null}
        </div>

        {def.kind === "content" ? (
          <>
            {def.part === "orders" && orderBook !== undefined ? (
              <>
                <CustomerOrderDestination label={orderBook.label} />
                <PaymentChoiceField
                  method={effective.paymentMethod ?? DEFAULT_PAYMENT_METHOD}
                  onlinePaymentOffered={props.onlinePaymentOffered}
                  error={shown[PROBLEM_KEY.payment]}
                  onMethod={(method) => {
                    change((current) => ({ ...current, paymentMethod: method }));
                  }}
                />
              </>
            ) : null}
            {/* ADR 0072: the step's own text, number, date and choice controls register as fill targets here; the
                website address, uploads, pasted text, money and the forward number never do (component rules). */}
            <HelpFillScope formId="new_agent_wizard">
            <WizardFields
              onBiIssue={setBiIssue}
              fields={contentFieldOfPart(card, def.part, answer.fieldValue)}
              context={context}
              shown={shown}
              answering={answering}
              answersAtAllHours={answersAtAllHours}
              forwardFact={props.forwardFact ?? null}
              menuPreview={shownPreview}
              onAnswer={change}
              onValue={setFieldValue}
              onFile={pickFile}
              onFiles={pickFiles}
              onRemoveFile={removeFile}
              onWeek={(code, week) => {
                change((current) => ({ ...current, openingWeek: { ...current.openingWeek, [code]: week } }));
              }}
            />
            </HelpFillScope>
          </>
        ) : null}

        {def.kind === "language" ? (
          <LanguageStep
            card={card}
            direction={direction}
            languagePack={props.languagePack}
            voice={props.voice}
            resolved={resolved}
            row={voiceRow}
            shown={shown}
            player={player}
            onAnswer={change}
          />
        ) : null}

        {def.kind === "medium" ? (
          <MediumStep
            direction={direction}
            webChatOn={webChatOn}
            webChatConnectable={props.webChatConnectable}
            recordingEnabled={answer.recordingEnabled}
            resolved={resolved}
            ceiling={props.ceiling}
            shown={shown}
            busyLine={asksOpeningHours(card) ? busyLineSentenceOf(answering.choice) : null}
            onAnswer={change}
          />
        ) : null}

        {isSummary ? (
          <SummaryStep
            context={context}
            resolved={resolved}
            answering={answering}
            row={voiceRow}
            steps={steps}
            direction={direction}
            upload={upload}
            webChatOn={webChatOn}
            ceiling={props.ceiling}
            shown={shown}
            player={player}
            menuItemCount={shownPreview?.phase === "done" ? (shownPreview.preview.itemCount ?? null) : null}
            onAnswer={change}
            onEdit={goTo}
          />
        ) : null}

        {isSummary && build.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {refusal === null ? apiErrorText(build.error, "") : refusal.text}
            {refusal === null || refusalStep === undefined ? null : (
              <>
                {" "}
                <button
                  type="button"
                  className="c-setup-inline-link"
                  onClick={() => {
                    setAttempted((current) => new Set([...current, refusalStep.step]));
                    goTo(refusalStep.step);
                  }}
                >
                  {portalText("core", "wizard.information.change_it")}
                </button>
              </>
            )}
          </p>
        ) : null}

        <div className="c-setup-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              const index = steps.findIndex((one) => one.step === def.step);
              const previous = steps[index - 1];
              if (previous === undefined) {
                void navigate({ to: "/agents/new" });
                return;
              }
              goTo(previous.step);
            }}
          >
            {portalText("operations", "back_b52b36b7")}</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={nextHint !== null || build.isPending}
            aria-disabled={nextHint !== null || build.isPending}
            onClick={() => {
              if (Object.keys(problem).length > 0) {
                setAttempted((current) => new Set([...current, def.step]));
                return;
              }
              if (!isSummary) {
                const index = steps.findIndex((one) => one.step === def.step);
                goTo(steps[index + 1]?.step ?? def.step);
                return;
              }
              build.mutate();
            }}
          >
            {!isSummary
              ? portalText("operations", "continue_2e026239")
              : build.isPending
                ? created === null
                  ? portalText("operations", "starting_the_build_329195f8")
                  : portalText("operations", "uploading_documents_e8dbf793")
                : created === null
                  ? portalText("operations", "build_the_agent_0599528b")
                  : portalText("operations", "try_again_042c862e")}
          </button>
          {nextHint === null ? null : <span className="c-setup-next-hint">{nextHint}</span>}
        </div>
        {Object.keys(shown).length > 0 ? (
          <p className="c-note c-note-critical" role="status">
            {Object.keys(shown).length === 1
              ? portalText("operations", "one_answer_above_needs_attention_before_you_continue_b4cae7c2")
              : portalText("operations", "value_answers_above_need_attention_before_you_continue_83e44266", { v0: String(String(Object.keys(shown).length)) })}
          </p>
        ) : null}
      </div>
    </section>
  );
}

// ── the step list ────────────────────────────────────────────────────────────────────────────

export interface SetupStepperProps {
  readonly agentTemplateName: string;
  readonly steps: readonly WizardStepDef[];
  readonly current: number;
  readonly onPicker: () => void;
  readonly onStep: (step: number) => void;
}

/** "Template" (done, back to the picker), then the template's steps: a tick when done, a ring when current. */
export function SetupStepper({ agentTemplateName, steps, current, onPicker, onStep }: SetupStepperProps): ReactNode {
  usePortalTranslation("operations");
  const row = [{ step: 1, label: portalText("operations", "template_3ec1ae06"), aria: portalText("operations", "template_value_8dc8d473", { v0: String(agentTemplateName) }) }, ...steps.map((one) => ({ step: one.step, label: one.label, aria: one.label }))];
  return (
    <ol aria-label={portalText("operations", "steps_cdde4f20")} className="c-setup-stepper">
      {row.map((one, index) => {
        const state = one.step < current ? "done" : one.step === current ? "current" : "todo";
        const aria = state === "done" ? portalText("operations", "done_value_85170f5d", { v0: String(one.aria) }) : state === "current" ? portalText("operations", "current_step_value_83556f09", { v0: String(one.aria) }) : portalText("operations", "step_value_value_b233327d", { v0: String(String(index + 1)), v1: String(one.aria) });
        return (
          <li key={one.step} className="c-setup-stepper-item">
            {index === row.length - 1 ? null : (
              <span aria-hidden="true" className={state === "done" ? "c-setup-stepper-sep c-setup-stepper-sep-done" : "c-setup-stepper-sep"} />
            )}
            <button
              type="button"
              className={`c-setup-stepper-step c-setup-stepper-step-${state}`}
              aria-current={state === "current" ? "step" : undefined}
              aria-label={aria}
              title={aria}
              tabIndex={state === "done" ? 0 : -1}
              onClick={() => {
                if (state !== "done") return;
                if (one.step === 1) onPicker();
                else onStep(one.step);
              }}
            >
              <span className="c-setup-stepper-mark">{state === "done" ? <Icon glyph={GLYPH.check} size="xs" /> : String(one.step)}</span>
              <span className="c-setup-stepper-label">{one.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ── content ──────────────────────────────────────────────────────────────────────────────────

interface WizardFieldsProps {
  readonly onBiIssue: (issue: string | null) => void;
  readonly fields: readonly AgentTemplateField[];
  readonly context: WizardContext;
  readonly shown: Readonly<Record<string, string>>;
  /** plan-015: the zone, special dates and choices drawn with the opening hours. */
  readonly answering: WizardAnswering;
  /** 2026-10-14: the engine answers at every hour — no "Out of hours" choice, and the sentence that says so. */
  readonly answersAtAllHours: boolean;
  readonly forwardFact: ForwardFact | null;
  /** plan-015 PRV: what was understood from the attached menu, shown under the menu's upload card; `null` before. */
  readonly menuPreview: MenuPreviewState | null;
  readonly onAnswer: (next: (current: WizardAnswer) => WizardAnswer) => void;
  readonly onValue: (code: string, value: string) => void;
  readonly onFile: (code: string, file: File) => void;
  /** plan-018: files added to an upload that takes several, and one taken out. */
  readonly onFiles: (field: AgentTemplateField, file: readonly File[]) => void;
  readonly onRemoveFile: (field: AgentTemplateField, name: string) => void;
  readonly onWeek: (code: string, week: OpeningWeek) => void;
}

/**
 * A step's fields, by kind and, for a `text` field, by format. A fee and a threshold share a row. A required group's
 * refusal is said once, under the last of its fields on the step (plan-018).
 */
function WizardFields({
  onBiIssue,
  fields,
  context,
  shown,
  answering,
  answersAtAllHours,
  forwardFact,
  menuPreview,
  onAnswer,
  onValue,
  onFile,
  onFiles,
  onRemoveFile,
  onWeek,
}: WizardFieldsProps): ReactNode {
  usePortalTranslation("operations");
  // The instant the step opened: "today" for refusing a past special date, read once rather than on every render.
  const [openedAtMs] = useState(() => Date.now());
  // Owner request 2026-10-18: the help assistant may propose ONE change set for the opening-hours editor while it is on
  // screen (here on Content, or on a template's Hours step) — every day, the zone and the special dates — written
  // through the editor's own setters below, so its validation, the draft and the Review line follow as for typing.
  const hoursField = fields.find((one) => one.kind === "text" && fieldFormatOf(one) === "opening_hours");
  const hoursZone = answering.timeZone;
  const hoursToday = calendarDateIn(hoursZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, openedAtMs);
  const hoursWeek = hoursField === undefined ? closedWeek() : (context.answer.openingWeek[hoursField.code] ?? closedWeek());
  const helpFill = useHelpFillable("new_agent_wizard", {
    opening_hours:
      hoursField === undefined
        ? undefined
        : {
            value: openingHoursFillOf(hoursWeek, hoursZone, answering.specialDate),
            set: (next) => {
              onWeek(hoursField.code, weekOfFill(hoursWeek, next));
              const zone = next.time_zone;
              if (zone !== undefined && zone !== hoursZone) onAnswer((current) => ({ ...current, timeZone: zone }));
              const dates = specialDateOfFill(next);
              if (dates !== null) onAnswer((current) => ({ ...current, specialDate: [...dates] }));
            },
            accepts: (next) =>
              openingHoursFillProblemOf(next, { timeZone: hoursZone, specialDate: answering.specialDate, today: hoursToday, maxShift: MAX_SHIFT_PER_DAY }) === null,
          },
  });
  const pairable = (field: AgentTemplateField): boolean => {
    const format = fieldFormatOf(field);
    return format === "amount" || format === "amount_or_none";
  };
  const row: (readonly AgentTemplateField[])[] = [];
  for (const one of fields) {
    const previous = row.at(-1);
    if (pairable(one) && previous !== undefined && previous.length === 1 && pairable(previous[0] as AgentTemplateField)) {
      row[row.length - 1] = [previous[0] as AgentTemplateField, one];
      continue;
    }
    row.push([one]);
  }
  const lastOfGroup = new Map<string, string>();
  for (const one of fields) if (one.requiredGroup !== undefined) lastOfGroup.set(one.requiredGroup, one.code);
  const groupError = (field: AgentTemplateField): ReactNode => {
    if (field.requiredGroup === undefined || lastOfGroup.get(field.requiredGroup) !== field.code) return null;
    const said = shown[requiredGroupKeyOf(field.requiredGroup)];
    return said === undefined ? null : (
      <p key={`${field.code}-group`} className="c-setup-group-error" role="alert">
        {said}
      </p>
    );
  };
  const control = (field: AgentTemplateField): ReactNode => {
    const drawn = fieldControl(field);
    const after = groupError(field);
    return after === null ? drawn : [drawn, after];
  };
  const fieldControl = (field: AgentTemplateField): ReactNode => {
    const error = shown[field.code];
    const errorProp = error === undefined ? {} : { error };
    if (field.kind === "text" && field.code === BI_CONFIGURATION_FIELD) {
      const uploadField = fields.find((one) => one.code === "bi_documents");
      return <BiConfigurationEditor onIssue={onBiIssue} key={field.code} value={context.answer.fieldValue[field.code] ?? ""} error={error} uploadNames={namedFileOf(context.answer.fieldValue["bi_documents"])} files={context.file} upload={uploadField === undefined ? undefined : fieldControl(uploadField)} onValue={(value) => onValue(field.code, value)} />;
    }
    if (field.kind === "website") {
      return (
        <WizardWebsite
          key={field.code}
          field={field}
          value={context.answer.fieldValue[field.code] ?? ""}
          error={error}
          onValue={(value) => {
            onValue(field.code, value);
          }}
        />
      );
    }
    if (field.kind === "upload" && takesSeveralFiles(field)) {
      return (
        <WizardFiles
          key={field.code}
          field={field}
          context={context}
          error={error}
          onFiles={(picked) => {
            onFiles(field, picked);
          }}
          onRemove={(name) => {
            onRemoveFile(field, name);
          }}
        />
      );
    }
    if (field.kind === "upload") {
      return (
        <WizardUpload
          key={field.code}
          field={field}
          label={uploadLabelOf(field, context.answer.fieldValue)}
          value={context.answer.fieldValue[field.code] ?? ""}
          picked={context.file[field.code]}
          attached={isAttached(context, field.code)}
          error={error}
          preview={field.code === MENU_PREVIEW_FIELD_CODE ? menuPreview : null}
          onFile={(picked) => {
            onFile(field.code, picked);
          }}
        />
      );
    }
    if (field.kind === "choice") {
      const value = context.answer.fieldValue[field.code] ?? field.defaultValue;
      const locked = context.created !== null && context.created.buildJobId !== "";
      return (
        <div key={field.code} className="field">
          <span className="c-setup-label" id={`${field.code}-label`}>
            {field.label}
          </span>
          <div role="radiogroup" aria-labelledby={`${field.code}-label`} className="c-setup-options c-setup-options-tight">
            {field.options.map((option) => (
              <label key={option.value} className={locked ? "c-setup-option c-setup-option-compact c-setup-option-off" : "c-setup-option c-setup-option-compact"}>
                <input
                  type="radio"
                  className="c-setup-option-input"
                  name={field.code}
                  value={option.value}
                  disabled={locked}
                  checked={value === option.value}
                  onChange={() => {
                    onValue(field.code, option.value);
                  }}
                />
                <span className="c-setup-option-title">{option.label}</span>
              </label>
            ))}
          </div>
          {locked ? <span className="wt-field-description">{portalText("operations", "use_start_over_to_change_this_choice_after_a_build_has_sta_ea479c58")}</span> : null}
          {error === undefined ? null : (
            <span className="wt-field-error" role="alert">
              {error}
            </span>
          )}
        </div>
      );
    }
    if (field.kind !== "text") return null;
    const format = fieldFormatOf(field);
    if (format === "opening_hours") {
      const zone = answering.timeZone;
      const today = calendarDateIn(zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, openedAtMs);
      // The export of 2026-10-10 draws no label over the week (`showLabel: f.kind !== 'hours'`): the step's title asks
      // the question. The label stays for a screen reader, and names the week's group.
      return (
        <div key={field.code} className="c-setup-group c-setup-hours" {...helpFill.mark("opening_hours")}>
          <Field label={field.label} {...errorProp}>
            {(slot) => (
              <OpeningWeekEditor
                id={slot.id}
                label={field.label}
                week={context.answer.openingWeek[field.code] ?? closedWeek()}
                timeZone={zone}
                describedBy={slot["aria-describedby"]}
                invalid={slot["aria-invalid"]}
                onWeek={(week) => {
                  onWeek(field.code, week);
                }}
                onTimeZone={(timeZone) => {
                  onAnswer((current) => ({ ...current, timeZone }));
                }}
                specialDate={{
                  entry: answering.specialDate,
                  today,
                  onEntry: (specialDate) => {
                    onAnswer((current) => ({ ...current, specialDate }));
                  },
                }}
              />
            )}
          </Field>
          <WizardAnsweringChoices
            choice={answering.choice}
            answersAtAllHours={answersAtAllHours}
            forwardFact={forwardFact}
            error={shown[PROBLEM_KEY.answering]}
            onChoice={(answeringChoice) => {
              onAnswer((current) => ({ ...current, answeringChoice }));
            }}
          />
        </div>
      );
    }
    return (
      <Field key={field.code} label={field.label} {...(format === null ? {} : { description: descriptionOf(format) })} {...errorProp}>
        {(slot) => (
          <Input
            {...slot}
            name={field.code}
            maxLength={field.maxLength}
            {...(format === null ? {} : { placeholder: placeholderOf(format), inputMode: "decimal" as const })}
            {...surveyPlaceholderOf(field.code)}
            value={context.answer.fieldValue[field.code] ?? ""}
            onChange={(event) => {
              onValue(field.code, event.currentTarget.value);
            }}
          />
        )}
      </Field>
    );
  };
  return (
    <>
      {row.filter((one) => !(one[0]?.code === "bi_documents" && fields.some((field) => field.code === BI_CONFIGURATION_FIELD))).map((one) =>
        one.length === 2 ? (
          <div key={(one[0] as AgentTemplateField).code} className="c-setup-pair">
            {one.map(control)}
          </div>
        ) : (
          control(one[0] as AgentTemplateField)
        ),
      )}
    </>
  );
}

/** `[".md", ".csv", ".pdf"]` → "MD, CSV or PDF." — the template's own `accept`, in words. */
function acceptHint(accept: readonly string[]): string {
  const word = accept.map((one) => one.replace(/^\./, "").toUpperCase());
  if (word.length === 0) return portalText("operations", "any_file_feb68b7f");
  if (word.length === 1) return `${String(word[0])}.`;
  return portalText("operations", "value_or_value_803267df", { v0: String(word.slice(0, -1).join(", ")), v1: String(String(word.at(-1))) });
}

/**
 * The survey's example answers, as the export's placeholders give them (design audit 2026-10-15). "Who is calling" has
 * none: the export's example there is its demo business, not copy.
 */
const SURVEY_TEXT_PLACEHOLDER: Readonly<Partial<Record<string, () => string>>> = {
  interviewer_name: () => portalText("operations", "merge.wizard.interviewer_name_placeholder"),
  survey_subject: () => portalText("operations", "merge.wizard.survey_subject_placeholder"),
};

function surveyPlaceholderOf(code: string): { readonly placeholder?: string } {
  const example = SURVEY_TEXT_PLACEHOLDER[code];
  return example === undefined ? {} : { placeholder: example() };
}

/** The survey's two questionnaire uploads (`SURVEY_FIELD_SET`): the export words their hint and the first one's label. */
const SURVEY_QUESTIONNAIRE_FIELD = new Set(["questionnaire", "questionnaire_arm2"]);

/**
 * The upload's label: the field's own, except the survey's first questionnaire with two groups, which the export calls
 * "Questions for group 1" beside "Questions for group 2" (design audit 2026-10-15).
 */
function uploadLabelOf(field: AgentTemplateField, value: Readonly<Record<string, string>>): string {
  return field.code === "questionnaire" && value["survey_arm_count"] === "2"
    ? portalText("operations", "merge.wizard.questions_for_group_1")
    : field.label;
}

/**
 * The dropzone's hint: the menu's and the questionnaire's as the export words them, any other upload's `accept` in
 * words. The questionnaire's leaves out the export's "Word": the upload takes JSON, CSV, Markdown and text only.
 */
function uploadHintOf(field: Extract<AgentTemplateField, { kind: "upload" }>): string {
  if (field.code === MENU_PREVIEW_FIELD_CODE) return MENU_HINT();
  if (SURVEY_QUESTIONNAIRE_FIELD.has(field.code)) return portalText("operations", "merge.wizard.questionnaire_hint");
  return acceptHint(field.accept);
}

export interface WizardUploadProps {
  readonly field: Extract<AgentTemplateField, { kind: "upload" }>;
  readonly label: string;
  readonly value: string;
  readonly picked: File | undefined;
  readonly attached: boolean;
  readonly error: string | undefined;
  /** plan-015 PRV: the menu's preview, drawn under the card (`null` for every other upload, and before one exists). */
  readonly preview: MenuPreviewState | null;
  readonly onFile: (file: File) => void;
  /**
   * Live bug 2026-10-28: puts back the document typed text replaced when that document is not a `File` this control can
   * hand back — the Edit panel's kept document. Absent in the wizard, where the replaced `File` itself is handed back.
   */
  readonly onKeep?: () => void;
}

/** The menu's dropzone hint, as the export of 2026-10-10 words it (the menu takes PDFs, spreadsheets and photos). */
const MENU_HINT = () => portalText("operations", "merge.wizard.a_pdf_a_spreadsheet_or_a_photo_of_your_printed_menu.e52cbd");


/**
 * The dropzone — a button that opens the file picker and takes a dropped file — and, for a field that may be typed
 * (`allowText`), "or type them here", which swaps it for a box whose text is uploaded as a plain-text file. The wizard
 * and the Edit panel both draw it.
 *
 * TYPED TEXT NEVER SILENTLY REPLACES A DOCUMENT (live bug 2026-10-28: an Edit meant to add a few rules to a kept
 * questionnaire replaced it with the rules alone, and the build found no questions). While the field holds a document
 * that is not this box's text — kept by Edit, or picked — the box says that what is typed replaces that file entirely,
 * and the text becomes the field's file only on "Replace … with this text". "Keep … instead" puts the document back.
 */
export function WizardUpload({ field, label, value, picked, attached, error, preview, onFile, onKeep }: WizardUploadProps): ReactNode {
  usePortalTranslation("operations");
  const picker = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const typedName = `${field.code}.txt`;
  // plan-018: what the typed box holds is the field's own copy (questions, or the business's information).
  const typedCopy = typedUploadCopyOf(field);
  const [typing, setTyping] = useState(field.allowText === true && picked?.name === typedName);
  const [typed, setTyped] = useState("");
  // The box's text is the field's file; otherwise `held` names the document the field holds, which typing would replace.
  // A name with nothing behind it (a reloaded draft's, "attach it again") holds nothing typing could lose.
  const confirmed = picked?.name === typedName;
  const held = !confirmed && value !== "" && attached ? value : null;
  // The document "Replace … with this text" set aside: its name, and the `File` to hand back when it was one.
  const [replaced, setReplaced] = useState<{ readonly name: string; readonly file: File | undefined } | null>(null);
  const shownReplaced = confirmed ? replaced : null;
  const handBack = shownReplaced?.file;
  const restore: (() => void) | null =
    shownReplaced === null ? null : handBack !== undefined ? () => { onFile(handBack); } : onKeep === undefined ? null : onKeep;
  const typedFileOf = (text: string): File => new File([text.trim() === "" ? "" : text], typedName, { type: "text/plain" });
  useEffect(() => {
    let active = true;
    if (field.allowText === true && picked?.name === typedName) {
      void picked.text().then((text) => {
        if (active) setTyped(text);
      });
    }
    return () => {
      active = false;
    };
  }, [field.allowText, picked, typedName]);

  const take = (files: FileList | null): void => {
    const first = files?.[0];
    if (first !== undefined) onFile(first);
  };
  const name = value === "" ? portalText("operations", "drop_a_file_here_or_browse_504715db") : attached ? portalText("operations", "value_attached_89bb2701", { v0: String(value) }) : portalText("operations", "value_attach_it_again_d36e90d0", { v0: String(value) });

  return (
    <Field label={label} {...(error === undefined ? {} : { error })}>
      {(control) => (
        <>
          {typing ? (
            <Textarea
              {...control}
              rows={typedCopy.row}
              placeholder={typedCopy.placeholder}
              value={typed}
              onChange={(event) => {
                const text = event.currentTarget.value;
                setTyped(text);
                // While another document is held, the text waits for "Replace … with this text".
                if (held === null) onFile(typedFileOf(text));
              }}
            />
          ) : (
            <button
              type="button"
              {...control}
              className={(value !== "" && attached) || over ? "c-setup-drop c-setup-drop-attached" : "c-setup-drop"}
              onClick={() => {
                picker.current?.click();
              }}
              onDragOver={(event: DragEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => {
                setOver(false);
              }}
              onDrop={(event: DragEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setOver(false);
                take(event.dataTransfer.files);
              }}
            >
              <span className="c-setup-drop-name">{name}</span>
              <span className="c-setup-drop-hint">{uploadHintOf(field)}</span>
            </button>
          )}
          {typing && held !== null ? (
            <div className="c-setup-replace">
              <p className="c-setup-replace-text" role="status">
                <Icon glyph={GLYPH.warningCircle} size="md" />
                <span>{typedCopy.replaces(held)}</span>
              </p>
              <Button
                variant="secondary"
                disabled={typed.trim() === ""}
                onClick={() => {
                  setReplaced({ name: held, file: picked });
                  onFile(typedFileOf(typed));
                }}
              >
                {portalText("operations", "wizard.typed_replace.confirm", { file: held })}
              </Button>
            </div>
          ) : null}
          {typing && shownReplaced !== null ? (
            <div className="c-setup-replace">
              <p className="c-setup-replace-text" role="status">
                <Icon glyph={GLYPH.warningCircle} size="md" />
                <span>{portalText("operations", "wizard.typed_replace.done", { file: shownReplaced.name })}</span>
              </p>
            </div>
          ) : null}
          {preview === null ? null : <MenuPreviewCard state={preview} />}
          {field.allowText === true ? (
            <button
              type="button"
              className="btn btn-ghost c-setup-type-toggle"
              onClick={() => {
                if (typing && restore !== null) {
                  restore();
                  setReplaced(null);
                }
                setTyping((now) => !now);
              }}
            >
              {!typing
                ? typedCopy.typeInstead
                : held !== null
                  ? portalText("operations", "wizard.typed_replace.keep", { file: held })
                  : shownReplaced !== null && restore !== null
                    ? portalText("operations", "wizard.typed_replace.keep", { file: shownReplaced.name })
                    : portalText("operations", "upload_a_file_instead_eaad9b48")}
            </button>
          ) : null}
          <input
            ref={picker}
            type="file"
            hidden
            tabIndex={-1}
            aria-hidden="true"
            name={field.code}
            accept={field.accept.join(",")}
            onChange={(event) => {
              take(event.currentTarget.files);
            }}
          />
        </>
      )}
    </Field>
  );
}

// ── plan-018 WAPP-A: the website address and several documents ────────────────────────────────

interface WizardWebsiteProps {
  readonly field: Extract<AgentTemplateField, { kind: "website" }>;
  readonly value: string;
  readonly error: string | undefined;
  readonly onValue: (value: string) => void;
}

/**
 * The website address: a URL box, the line saying what is read, and — once the person leaves the box — the address
 * written in the one form it is stored in ("roma-pizzeria.gr" becomes "https://roma-pizzeria.gr/"), so what the Review
 * step shows is what the build reads. A refused address keeps what was typed; its sentence is said on Continue.
 */
function WizardWebsite({ field, value, error, onValue }: WizardWebsiteProps): ReactNode {
  usePortalTranslation("core");
  return (
    <Field label={field.label} description={websiteDescriptionText()} {...(error === undefined ? {} : { error })}>
      {(slot) => (
        <Input
          {...slot}
          type="url"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          name={field.code}
          maxLength={2048}
          placeholder={websitePlaceholderText()}
          value={value}
          onChange={(event) => {
            onValue(event.currentTarget.value);
          }}
          onBlur={(event) => {
            // What the box holds now — a render may not have caught up with the last keystroke.
            const typed = event.currentTarget.value;
            const sent = websiteAddressSentOf(typed);
            if (sent !== null && sent !== typed) onValue(sent);
          }}
        />
      )}
    </Field>
  );
}

interface WizardFilesProps {
  readonly field: Extract<AgentTemplateField, { kind: "upload" }>;
  readonly context: WizardContext;
  readonly error: string | undefined;
  readonly onFiles: (file: readonly File[]) => void;
  readonly onRemove: (name: string) => void;
}

/** The extensions of `accept` the upload can send — a type the factory cannot read is not offered in the hint. */
function sendableAcceptOf(accept: readonly string[]): readonly string[] {
  return accept.filter((one) => contentTypeOfFile({ name: `file${one}`, type: "" }) !== null);
}

/**
 * An upload that takes several files (`maxFileCount`): the files given so far, each with Remove, and under them the
 * dropzone — taking several at once — while there is room. A file the agent cannot read, or one past the limit, is
 * left out at once and said, rather than failing the build later.
 */
function WizardFiles({ field, context, error, onFiles, onRemove }: WizardFilesProps): ReactNode {
  usePortalTranslation("core");
  const picker = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const most = fileLimitOf(field);
  const named = namedFileOf(context.answer.fieldValue[field.code]);
  const attached = attachedNameOf(context, field);
  const sendable = sendableAcceptOf(field.accept);
  const room = most - named.length;

  const take = (files: FileList | null): void => {
    const all = [...(files ?? [])];
    const unreadable = all.filter((one) => contentTypeOfFile(one) === null);
    const readable = all.filter((one) => contentTypeOfFile(one) !== null);
    // A file of a name already given replaces it and takes no new room.
    const fresh = readable.filter((one) => !named.includes(one.name));
    const kept = [...readable.filter((one) => named.includes(one.name)), ...fresh.slice(0, Math.max(0, room))];
    const leftOut = fresh.slice(Math.max(0, room));
    const said = [
      ...(unreadable.length === 0 ? [] : [fileUnreadableText(unreadable.map((one) => one.name))]),
      ...(leftOut.length === 0 ? [] : [filesLeftOutText(most, leftOut.map((one) => one.name))]),
    ];
    setNotice(said.length === 0 ? null : said.join(" "));
    if (kept.length > 0) onFiles(kept);
  };

  return (
    <Field label={field.label} {...(error === undefined ? {} : { error })}>
      {(control) => (
        <>
          {named.length === 0 ? null : (
            <ul className="c-setup-files" aria-label={field.label}>
              {named.map((name) => {
                const here = attached.includes(name);
                return (
                  <li key={name} className={here ? "c-setup-file c-setup-file-attached" : "c-setup-file"}>
                    <span className="c-setup-file-name">
                      {here
                        ? portalText("operations", "value_attached_89bb2701", { v0: name })
                        : portalText("operations", "value_attach_it_again_d36e90d0", { v0: name })}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost c-setup-small-ghost"
                      aria-label={removeFileText(name)}
                      onClick={() => {
                        setNotice(null);
                        onRemove(name);
                      }}
                    >
                      {removeText()}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {room > 0 ? (
            <button
              type="button"
              {...control}
              className={over ? "c-setup-drop c-setup-drop-attached" : "c-setup-drop"}
              onClick={() => {
                picker.current?.click();
              }}
              onDragOver={(event: DragEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => {
                setOver(false);
              }}
              onDrop={(event: DragEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setOver(false);
                take(event.dataTransfer.files);
              }}
            >
              <span className="c-setup-drop-name">{dropFilesText()}</span>
              <span className="c-setup-drop-hint">{fileLimitHintText(most, sendable)}</span>
            </button>
          ) : (
            <p id={control.id} className="c-setup-preview-note">
              {fileLimitReachedText(most)}
            </p>
          )}
          {notice === null ? null : (
            <p className="c-setup-preview-note" role="status">
              {notice}
            </p>
          )}
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            tabIndex={-1}
            aria-hidden="true"
            name={field.code}
            accept={sendable.join(",")}
            onChange={(event) => {
              take(event.currentTarget.files);
              // The same file chosen again after a removal still fires a change.
              event.currentTarget.value = "";
            }}
          />
        </>
      )}
    </Field>
  );
}

/**
 * plan-015 PRV (gap G6, design `WeTalk.dc.html` 3191–3199): what WeTalk understood from the attached menu — "We found
 * 42 dishes and 11 extras (toppings and add-ons).", the first items with their prices, and "and 38 more. Something
 * wrong? Fix your file and upload it again." (the export of 2026-10-10). While the menu is read, one quiet line says so; a menu that could not be read says that plainly.
 * None of it blocks Continue: the build reads the menu itself.
 */
function MenuPreviewCard({ state }: { readonly state: MenuPreviewState }): ReactNode {
  usePortalTranslation("operations");
  if (state.phase === "reading") {
    return (
      <p className="c-setup-preview-note" role="status">
        {portalText("operations", "reading_your_menu_111172ed")}</p>
    );
  }
  if (state.phase === "failed") {
    return (
      <p className="c-setup-preview-note" role="status">
        {state.message}
      </p>
    );
  }
  const preview = state.preview;
  return (
    <div className="c-setup-preview" role="status">
      <span className="c-setup-preview-head">
        <Icon glyph={GLYPH.checkCircle} size="lg" tone="accent" />
        {menuPreviewHeadText(preview)}
      </span>
      <ul className="c-setup-preview-list">
        {preview.sample.map((one, index) => (
          <li key={`${one.name}-${String(index)}`} className="c-setup-preview-item">
            <span className="c-setup-preview-name">{one.name}</span>
            <span className="c-setup-preview-price">{menuSamplePriceText(one)}</span>
          </li>
        ))}
      </ul>
      <span className="c-setup-preview-foot">{menuPreviewFootText(preview)}</span>
    </div>
  );
}

/**
 * Where orders go: the WeTalk order book — the destination that works (#3, #126) — and the shop's own system, drawn as
 * the design draws it and not choosable until it is built (a gap the design sync reports): its tag says "Coming soon"
 * where the export's says "Advanced", because it cannot be chosen yet.
 */
function CustomerOrderDestination({ label }: { readonly label: string }): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="c-setup-options" role="radiogroup" aria-label={label}>
      <label className="c-setup-option">
        <input type="radio" className="c-setup-option-input" name="order-destination" value="book" checked readOnly />
        <Icon glyph={GLYPH.receipt} size="3xl" tone="accent" />
        <span className="c-setup-option-body">
          <span className="c-setup-option-head">
            <span className="c-setup-option-name">{portalText("operations", "wetalk_order_book_a2d90ad7")}</span>
            <span className="c-setup-option-tag">
              <Tag tone="neutral">{portalText("operations", "recommended_9ef93755")}</Tag>
            </span>
          </span>
          <span className="c-setup-option-dek">{portalText("operations", "merge.wizard.orders_appear_on_a_tablet_or_computer_in_your_shop_and_in_the_agen.ebaec5")}</span>

        </span>
      </label>
      <label className="c-setup-option c-setup-option-off">
        <input type="radio" className="c-setup-option-input" name="order-destination" value="api" checked={false} disabled readOnly />
        <Icon glyph={GLYPH.plugsConnected} size="3xl" tone="accent" />
        <span className="c-setup-option-body">
          <span className="c-setup-option-head">
            <span className="c-setup-option-name">{portalText("operations", "merge.wizard.straight_into_my_order_system.f30636")}</span>

            <span className="c-setup-option-tag">
              <Tag tone="neutral">{portalText("operations", "coming_soon_e4115be2")}</Tag>
            </span>
          </span>
          <span className="c-setup-option-dek">{portalText("operations", "merge.wizard.for_shops_whose_till_or_order_system_can_receive_orders_from_other.c66325")}</span>

        </span>
      </label>
    </div>
  );
}

interface PaymentChoiceFieldProps {
  readonly method: readonly PaymentMethodCode[];
  readonly onlinePaymentOffered: boolean;
  readonly error: string | undefined;
  readonly onMethod: (method: readonly PaymentMethodCode[]) => void;
}

/** How customers pay: three cards; the checkout link only while the account's online payment is on. */
function PaymentChoiceField({ method, onlinePaymentOffered, error, onMethod }: PaymentChoiceFieldProps): ReactNode {
  usePortalTranslation("operations");
  const chosen = paymentChoiceOf(method);
  return (
    <div className="field">
      <span className="c-setup-label" id="payment-choice-label">
        {portalText("operations", "how_customers_pay_70db6c7d")}</span>
      {/* The export names the group "Payment during the call" for a screen reader, under the visible "How customers pay". */}
      <div role="radiogroup" aria-label={portalText("operations", "merge.wizard.payment_during_the_call")} className="c-setup-options c-setup-options-tight">
        {PAYMENT_OPTION.map((one) => {
          const off = one.choice === "link" && !onlinePaymentOffered;
          return (
            <label key={one.choice} className={off ? "c-setup-option c-setup-option-pay c-setup-option-off" : "c-setup-option c-setup-option-pay"}>
              <input
                type="radio"
                className="c-setup-option-input"
                name="payment-choice"
                value={one.choice}
                checked={chosen === one.choice}
                disabled={off}
                onChange={() => {
                  onMethod(PAYMENT_CHOICE_METHOD[one.choice]);
                }}
              />
              <span>
                <span className="c-setup-option-line">{one.label}</span>
                {off ? <span className="c-setup-option-hint">{portalText("operations", "turn_on_online_payments_in_billing_to_send_checkout_links_5276ac61")}</span> : null}
              </span>
            </label>
          );
        })}
      </div>
      {error === undefined ? null : (
        <span className="wt-field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

// ── languages and voice ──────────────────────────────────────────────────────────────────────

interface LanguageStepProps {
  readonly card: AgentTemplateCardView;
  readonly direction: ConversationDirection;
  readonly languagePack: readonly LanguagePackView[];
  readonly voice: readonly VoiceView[];
  readonly resolved: Resolved;
  readonly row: readonly LanguageVoiceRow[];
  readonly shown: Readonly<Record<string, string>>;
  readonly player: VoiceSamplePlayer;
  readonly onAnswer: (next: (current: WizardAnswer) => WizardAnswer) => void;
}

function LanguageStep({ card, direction, languagePack, voice, resolved, row, shown, player, onAnswer }: LanguageStepProps): ReactNode {
  usePortalTranslation("operations");

  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const oneLanguage = speaksOneLanguage(card);
  const chosen = resolved.languageCode;
  const primary = chosen[0];
  const outbound = direction === "outbound";
  const setLanguages = (next: readonly string[]): void => {
    onAnswer((current) => ({ ...current, languageCode: [...next] }));
  };
  // Only languages a voice speaks can be offered: an agent cannot answer in a language nobody speaks.
  const offered = languagePack.filter((pack) => voice.some((one) => one.supportedLanguageCode.includes(pack.code)));
  // plan-019 WAPP-FORM: the help assistant may propose the languages while this step is on screen — only languages a
  // voice speaks, one for an agent that speaks one, through the same setter as the chips (research form-fill §5a).
  const helpFill = useHelpFillable("new_agent_wizard", {
    language_code: {
      value: chosen,
      set: setLanguages,
      accepts: (next) =>
        next.length > 0 &&
        (!oneLanguage || next.length === 1) &&
        new Set(next).size === next.length &&
        next.every((code) => offered.some((pack) => pack.code === code)),
    },
  });
  const listed = offered.filter((pack) => search.trim() === "" || pack.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  // plan-026 (0176): the note names the beta languages this step offers, from the catalogue's maturity; none, no note.
  const betaNote = languageBetaNoteOf(offered);
  return (
    <>
      <div className="field">
        <span className="c-setup-label c-setup-label-strong" id="language-label">
          {portalText("operations", "merge.wizard.languages_selected", { count: chosen.length })}
        </span>

        <div role="group" aria-labelledby="language-label" className="c-setup-lang" {...helpFill.mark("language_code")}>
          {chosen.map((code) => {
            const name = languageNameOf(languagePack, code);
            return (
              <span key={code} className="c-setup-lang-chip">
                {name}
                <LanguageBetaMark pack={languagePack} code={code} />
                <button
                  type="button"
                  className="c-setup-lang-remove"
                  aria-label={portalText("operations", "remove_value_f7ef029d", { v0: String(name) })}
                  onClick={() => {
                    setLanguages(chosen.filter((one) => one !== code));
                  }}
                >
                  <Icon glyph={GLYPH.x} size="xs" />
                </button>
              </span>
            );
          })}
          <span className="c-setup-lang-add-wrap">
            <button
              type="button"
              className="c-setup-lang-add"
              aria-expanded={adding}
              aria-haspopup="listbox"
              onClick={() => {
                setAdding((now) => !now);
                setSearch("");
              }}
            >
              <Icon glyph={GLYPH.plus} size="xs" />
              {portalText("operations", "add_language_cb78db4b")}</button>
            {adding ? (
              <>
                <div
                  className="c-setup-lang-scrim"
                  aria-hidden="true"
                  onClick={() => {
                    setAdding(false);
                  }}
                />
                <div
                  className="c-setup-lang-pop"
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setAdding(false);
                  }}
                >
                  <input
                    className="input c-setup-lang-search"
                    placeholder={portalText("operations", "search_languages_ea93e617")}
                    aria-label={portalText("operations", "search_languages_ea93e617")}
                    value={search}
                    onChange={(event) => {
                      setSearch(event.currentTarget.value);
                    }}
                  />
                  <div role="listbox" aria-label={portalText("operations", "languages_db07be18")} className="c-setup-lang-list">
                    {listed.map((pack) => {
                      const on = chosen.includes(pack.code);
                      return (
                        <button
                          type="button"
                          role="option"
                          key={pack.code}
                          aria-selected={on}
                          className="c-setup-lang-option"
                          onClick={() => {
                            if (on) {
                              if (chosen.length > 1) setLanguages(chosen.filter((one) => one !== pack.code));
                              return;
                            }
                            setLanguages(oneLanguage ? [pack.code] : [...chosen, pack.code]);
                          }}
                        >
                          <span className="c-setup-lang-option-name">
                            {pack.name}
                            <LanguageBetaMark pack={languagePack} code={pack.code} />
                          </span>
                          {on ? <Icon glyph={GLYPH.check} size="sm" tone="accent" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : null}
          </span>
        </div>
        {oneLanguage ? <span className="c-setup-hint">{card.name} {portalText("operations", "agents_speak_one_language_for_now_96aa2578")}</span> : null}
        {betaNote === null ? null : <span className="c-setup-hint">{betaNote}</span>}
        {chosen.length === 0 ? null : (
          <>
            <div className="c-setup-greets">
              <label htmlFor="greeting-language">{outbound ? portalText("operations", "greets_the_person_it_calls_in_135b178d") : portalText("operations", "greets_callers_in_21ee1f61")}</label>
              <span className="c-setup-select c-setup-select-inline">
                <select
                  id="greeting-language"
                  aria-label={portalText("operations", "merge.wizard.greeting_language")}
                  className="input"
                  value={primary}
                  onChange={(event) => {
                    const code = event.currentTarget.value;
                    setLanguages([code, ...chosen.filter((one) => one !== code)]);
                  }}
                >
                  {chosen.map((code) => (
                    <option key={code} value={code}>
                      {betaLanguageLabelOf(languagePack, code, languageNameOf(languagePack, code))}
                    </option>
                  ))}
                </select>
                <Icon glyph={GLYPH.caretDown} size="xs" />
              </span>
            </div>
            <span className="c-setup-hint">
              {outbound ? portalText("operations", "if_they_answer_in_one_of_the_other_languages_the_agent_swi_444b36d1") : portalText("operations", "if_a_caller_speaks_one_of_the_other_languages_the_agent_sw_b6e725d1")}
            </span>
          </>
        )}
        {shown[PROBLEM_KEY.language] === undefined ? null : (
          <span className="wt-field-error" role="alert">
            {shown[PROBLEM_KEY.language]}
          </span>
        )}
      </div>


      <LanguageVoiceRows
        row={row}
        greeting={resolved.greeting}
        player={player}
        error={shown[PROBLEM_KEY.voice]}
        onVoice={(languageCode, voiceId) => {
          onAnswer((current) => ({ ...current, voiceByLanguage: { ...(current.voiceByLanguage ?? {}), [languageCode]: voiceId } }));
        }}
      />
    </>
  );
}

export interface LanguageVoiceRowsProps {
  /** One row per chosen language, the greeting language first (`Resolved.voiceRow`). */
  readonly row: readonly LanguageVoiceRow[];
  /** What the agent says first; the greeting language's Play speaks it (plan-015 GRT). */
  readonly greeting: string;
  readonly player: VoiceSamplePlayer;
  readonly error: string | undefined;
  readonly onVoice: (languageCode: string, voiceId: string) => void;
}

/**
 * "Voices" (`design/WeTalk.dc.html` 3306–3328, the export of 2026-10-10): one card listing the chosen languages, a row
 * each — its name with "Greets callers" under the greeting language, a dropdown naming the voice chosen ("Mara · Warm
 * and clear") whose list shows every voice that speaks it with a tick on the one chosen, and a round Play. Agent
 * setup's Languages step and the agent page's Edit panel draw the same rows (plan-015 G1U), so an edit chooses a voice
 * per language exactly as the build did. The greeting language's Play says the greeting (plan-015 GRT, owner answer
 * 11); the others say the voice's sample line.
 */
export function LanguageVoiceRows({ row, greeting, player, error, onVoice }: LanguageVoiceRowsProps): ReactNode {
  usePortalTranslation("operations");
  if (row.length === 0) return null;
  return (
    <div className="field">
      <span className="c-setup-label c-setup-label-strong" id="voices-label">
        {portalText("operations", "voices_40273160")}</span>
      <span className="c-setup-hint c-setup-hint-lead">{portalText("operations", "each_language_has_its_own_voices_we_picked_one_for_each_ch_05557e61")}</span>
      <div role="group" aria-labelledby="voices-label" className="c-setup-voice-rows">

        {row.map((one, index) => (
          <VoiceRow
            key={one.languageCode}
            row={one}
            greets={index === 0}
            greeting={index === 0 ? greeting : null}
            player={player}
            onVoice={(voiceId) => {
              onVoice(one.languageCode, voiceId);
            }}
          />
        ))}
      </div>
      {error === undefined ? null : (
        <span className="wt-field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/** "warm and clear" → "Warm and clear": the export capitalises a voice's description in its list. */
function voiceDescriptionOf(description: string): string {
  const trimmed = description.trim();
  return trimmed === "" ? "" : `${trimmed.charAt(0).toLocaleUpperCase()}${trimmed.slice(1)}`;
}

function VoiceRow({ row, greets, greeting, player, onVoice }: {
  readonly row: LanguageVoiceRow;
  readonly greets: boolean;
  /** plan-015 GRT: the greeting language's row plays the voice saying this greeting. */
  readonly greeting: string | null;
  readonly player: VoiceSamplePlayer;
  readonly onVoice: (voiceId: string) => void;
}): ReactNode {
  usePortalTranslation("operations");
  const [open, setOpen] = useState(false);

  const chosen = row.option.find((one) => one.voiceId === row.voiceId);
  const chosenName = chosen?.name ?? portalText("operations", "no_voice_speaks_it_yet_521c2adc");
  const chosenDescription = chosen === undefined ? "" : voiceDescriptionOf(chosen.description);
  // plan-026 (0176): "Female" / "Male" beside the name, so several voices of one language can be told apart.
  const chosenGender = chosen === undefined ? null : voiceGenderTextOf(chosen.gender);
  const unavailable = player.unavailable.has(playKeyOf(row.voiceId, row.languageCode, greeting));
  return (
    <div className="c-setup-voice-lang">
      <span className="c-setup-voice-lang-name">
        <span className="c-setup-voice-lang-title">
          <span className="c-setup-voice-lang-strong">{row.languageName}</span>
          {row.beta ? <LanguageBetaTag /> : null}
        </span>
        {greets ? (
          <span className="c-setup-voice-greets">
            <Tag tone="accent">{portalText("operations", "merge.wizard.greets_callers.3f1c6f")}</Tag>
          </span>
        ) : null}
      </span>
      <span className="c-setup-voice-pick">
        <button
          type="button"
          className="c-setup-voice-button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={portalText("operations", "merge.wizard.voice_for_v0_v1.e0b7cf", { v0: String(row.languageName), v1: String(chosenName) })}

          disabled={row.option.length === 0}
          onClick={() => {
            setOpen((now) => !now);
          }}
        >
          <span className="c-setup-voice-name">{chosenName}</span>{" "}
          {chosenGender === null ? null : <span className="c-setup-voice-gender">· {chosenGender}</span>}{chosenGender === null ? null : " "}
          {chosenDescription === "" ? null : <span className="c-setup-voice-desc">· {chosenDescription}</span>}
          <Icon glyph={GLYPH.caretDown} size="xs" />
        </button>
        {open ? (
          <>
            <div
              className="c-setup-lang-scrim"
              aria-hidden="true"
              onClick={() => {
                setOpen(false);
              }}
            />
            <div
              role="listbox"
              aria-label={portalText("operations", "merge.wizard.voices_for_v0.46497d", { v0: String(row.languageName) })}
              className="c-setup-voice-list"
              onKeyDown={(event) => {
                if (event.key === "Escape") setOpen(false);
              }}
            >
              {row.option.map((one) => {
                const selected = one.voiceId === row.voiceId;
                const description = voiceDescriptionOf(one.description);
                const gender = voiceGenderTextOf(one.gender);
                return (
                  <button
                    type="button"
                    role="option"
                    key={one.voiceId}
                    aria-selected={selected}
                    className={selected ? "c-setup-voice-option c-setup-voice-option-on" : "c-setup-voice-option"}
                    onClick={() => {
                      onVoice(one.voiceId);
                      setOpen(false);
                    }}
                  >
                    <span>{one.name}</span>{" "}
                    {gender === null ? null : <span className="c-setup-voice-gender">· {gender}</span>}{gender === null ? null : " "}
                    {description === "" ? null : <span className="c-setup-voice-desc">· {description}</span>}
                    {selected ? <Icon glyph={GLYPH.check} size="sm" tone="accent" /> : null}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}

      </span>
      <LanguageVoicePlayButton
        player={player}
        voiceId={row.voiceId}
        voiceName={chosen?.name ?? ""}
        languageCode={row.languageCode}
        languageName={row.languageName}
        compact
        round
        greeting={greeting}
      />
      {unavailable ? (
        <span className="c-setup-voice-unavailable c-setup-voice-row-note" role="status">
          {portalText("operations", "sample_unavailable_1ea3d37a")}</span>
      ) : null}
    </div>
  );
}

// ── channels ─────────────────────────────────────────────────────────────────────────────────

interface MediumStepProps {
  readonly direction: ConversationDirection;
  readonly webChatOn: boolean;
  readonly webChatConnectable: boolean;
  /** "Record calls" (RC-47; owner decision 2026-10-09: it stays a choice in Agent setup), on by default. */
  readonly recordingEnabled: boolean;
  readonly resolved: Resolved;
  readonly ceiling: number;
  readonly shown: Readonly<Record<string, string>>;
  /**
   * plan-015 G8: what a caller hears when every line is busy, from the busy choice and the longest wait the build will
   * use; `null` for a template that asks no opening hours (and so states no choice).
   */
  readonly busyLine: string | null;
  readonly onAnswer: (next: (current: WizardAnswer) => WizardAnswer) => void;
}

function MediumStep({
  direction,
  webChatOn,
  webChatConnectable,
  recordingEnabled,
  resolved,
  ceiling,
  shown,
  busyLine,
  onAnswer,
}: MediumStepProps): ReactNode {
  usePortalTranslation("operations");
  const [quotaOpen, setQuotaOpen] = useState(false);
  const outbound = direction === "outbound";
  const chanHelp = outbound
    ? portalText("operations", "both_channels_use_the_same_questions_and_languages_3be3e59c")
    : portalText("operations", "merge.wizard.a_chat_box_on_your_website_where_customers_can_type_their_order_yo.ca212c");
  const share = outbound ? portalText("operations", "the_agent_makes_up_to_value_calls_at_the_same_time_f7db569c", { v0: String(String(resolved.channelUnitCount)) }) : webChatOn ? portalText("operations", "calls_and_chats_share_these_lines_b9f83b80") : "";
  // 2026-10-18: the help assistant may propose the channels (calls always, website chat while it can be switched on) and
  // the calls at once (only a count the organization allows), through the same changes as the controls below.
  // Recording is never proposed: callers hear a notice because of it, so it stays the person's own choice.
  const offeredCount = channelUnitChoiceOf(ceiling).filter((one) => !one.over).map((one) => String(one.count));
  const helpFill = useHelpFillable("new_agent_wizard", {
    medium: {
      value: webChatOn ? ["voice", "web_chat"] : ["voice"],
      set: (next) => {
        onAnswer((current) => ({ ...current, medium: next.includes("web_chat") ? ["voice", "web_chat"] : ["voice"] }));
      },
      accepts: (next) => next.includes("voice") && (!next.includes("web_chat") || webChatConnectable),
    },
    channel_unit_count: {
      value: String(resolved.channelUnitCount),
      set: (next) => {
        onAnswer((current) => ({ ...current, channelUnitCount: Number(next) }));
      },
      accepts: (next) => offeredCount.includes(next),
    },
  });

  return (
    <>
      <div className="c-setup-group">
        <h2 className="c-setup-h2">{portalText("operations", "where_it_answers_6b2187f9")}</h2>
        <div className="c-setup-options" {...helpFill.mark("medium")}>
          <div className="c-setup-option c-setup-option-static">
            <Icon glyph={GLYPH.phone} size="3xl" tone="accent" />
            <span className="c-setup-option-body">
              <span className="c-setup-option-head">
                <span className="c-setup-option-name">{portalText("operations", "calls_0a19b7e2")}</span>
                <span className="c-setup-option-tag">
                  <Tag tone="neutral">{portalText("operations", "always_on_531e4565")}</Tag>
                </span>
              </span>
              <span className="c-setup-option-dek">
                {outbound ? portalText("operations", "from_your_calling_number_to_the_people_in_each_campaign_292d2c2f") : portalText("operations", "on_your_phone_number_and_the_voice_button_on_your_website_10dc8c36")}
              </span>
            </span>
          </div>
          <label className={webChatConnectable ? "c-setup-option c-setup-option-check" : "c-setup-option c-setup-option-check c-setup-option-off"}>
            <input
              type="checkbox"
              className="c-setup-option-input"
              checked={webChatOn}
              disabled={!webChatConnectable}
              onChange={(event) => {
                const next = event.currentTarget.checked;
                onAnswer((current) => ({ ...current, medium: next ? ["voice", "web_chat"] : ["voice"] }));
              }}
            />
            <Icon glyph={GLYPH.chatCircleText} size="3xl" tone="accent" />
            <span className="c-setup-option-body">
              <span className="c-setup-option-name">{portalText("operations", "merge.wizard.website_chat.772d13")}</span>
              <span className="c-setup-option-dek">{webChatConnectable ? chanHelp : portalText("operations", "merge.wizard.v0_website_chat_is_coming_soon.18e082", { v0: String(chanHelp) })}</span>

            </span>
          </label>
        </div>
        {webChatOn ? (
          <p className="c-setup-callout">
            <Icon glyph={GLYPH.code} size="lg" />
            {portalText("operations", "you_ll_get_a_code_to_add_the_chat_to_your_website_after_bu_38282f77")}</p>
        ) : null}
        {/* Owner decision 2026-10-09: recording stays a choice here, drawn like the web-chat row, sent as
            `recording_enabled` exactly as the round-D wizard did. */}
        <label className="c-setup-option c-setup-option-check">
          <input
            type="checkbox"
            className="c-setup-option-input"
            checked={recordingEnabled}
            onChange={(event) => {
              const next = event.currentTarget.checked;
              onAnswer((current) => ({ ...current, recordingEnabled: next }));
            }}
          />
          <Icon glyph={GLYPH.microphone} size="3xl" tone="accent" />
          <span className="c-setup-option-body">
            <span className="c-setup-option-name">{portalText("operations", "record_calls_37ca242a")}</span>
            <span className="c-setup-option-dek">
              {portalText("operations", "when_on_callers_hear_a_short_notice_after_the_greeting_and_7abae0f0")}</span>
          </span>
        </label>
        {shown[PROBLEM_KEY.medium] === undefined ? null : (
          <span className="wt-field-error" role="alert">
            {shown[PROBLEM_KEY.medium]}
          </span>
        )}
      </div>
      <div className="c-setup-group c-setup-section">
        <h2 className="c-setup-h2">{portalText("operations", "merge.wizard.how_many_at_once.dd6180")}</h2>

        <div className="field">
          <span className="c-setup-label" id="capacity-label">
            {!outbound && webChatOn ? portalText("operations", "calls_and_chats_at_once_57bade7b") : portalText("operations", "calls_at_once_cb8a5b2e")}
          </span>
          <span className="c-setup-hint c-setup-hint-lead">{portalText("operations", "merge.wizard.how_many_customers_the_agent_talks_to_at_the_same_time.e88408")}</span>
          <div className="seg c-setup-seg" role="radiogroup" aria-labelledby="capacity-label" {...helpFill.mark("channel_unit_count")}>
            {channelUnitChoiceOf(ceiling).map((one) => (
              <label
                key={one.count}
                className={one.over ? "seg-opt c-setup-seg-over" : "seg-opt"}
                title={one.over ? portalText("operations", "above_your_organization_s_limit_5cf132fe") : undefined}
              >
                <input
                  type="radio"
                  name="channelUnitCount"
                  value={String(one.count)}
                  checked={resolved.channelUnitCount === one.count}
                  disabled={one.over}
                  onChange={() => {
                    onAnswer((current) => ({ ...current, channelUnitCount: one.count }));
                  }}
                />
                <span>{String(one.count)}</span>
              </label>
            ))}
          </div>
        </div>
        <span className="c-setup-hint">
          {share}{portalText("operations", "your_organization_allows_897e2dd0")}{String(ceiling)} {portalText("operations", "at_once_across_all_agents_e6593c97")}{" "}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              setQuotaOpen(true);
            }}
          >
            {portalText("operations", "request_more_lines_cba2054b")}</a>
        </span>
        {outbound || busyLine === null ? null : <span className="c-setup-hint">{busyLine}</span>}
        {shown[PROBLEM_KEY.channelUnit] === undefined ? null : (
          <span className="wt-field-error" role="alert">
            {shown[PROBLEM_KEY.channelUnit]}
          </span>
        )}
        <QuotaRequestDialog
          open={quotaOpen}
          currentCeiling={ceiling}
          onClose={() => {
            setQuotaOpen(false);
          }}
        />
      </div>
    </>
  );
}

// ── review ───────────────────────────────────────────────────────────────────────────────────

interface SummaryRow {
  readonly term: string;
  readonly detail: string;
  /** A voice row: its Play. */
  readonly play?: { readonly voiceId: string; readonly voiceName: string; readonly row: LanguageVoiceRow; readonly greeting: string | null };
}

interface SummaryCard {
  readonly title: string;
  readonly step: number;
  readonly row: readonly SummaryRow[];
}

interface SummaryStepProps {
  readonly context: WizardContext;
  readonly resolved: Resolved;
  readonly answering: WizardAnswering;
  readonly row: readonly LanguageVoiceRow[];
  readonly steps: readonly WizardStepDef[];
  readonly direction: ConversationDirection;
  readonly upload: Readonly<Record<string, DocumentUploadProgress>>;
  readonly webChatOn: boolean;
  readonly ceiling: number;
  readonly shown: Readonly<Record<string, string>>;
  readonly player: VoiceSamplePlayer;
  /** The menu preview's item count, when the attached menu has been read (`null` before, and for any other template). */
  readonly menuItemCount: number | null;
  readonly onAnswer: (next: (current: WizardAnswer) => WizardAnswer) => void;
  readonly onEdit: (step: number) => void;
}

/**
 * The Review rows as the export words them (design audit 2026-10-15): the delivery menu's row is "Menu", with the
 * preview's item count when it has one ("menu.pdf · 42 items"); the survey's are "Questions" (both groups' files, and
 * "· 2 groups" when two, or "Typed in · 6 questions"), "Agent's name", "Topic" and "Who's calling", the arm choice and the
 * second group folded into "Questions". Display only: what is sent is unchanged.
 */
const SURVEY_SUMMARY_TERM: Readonly<Partial<Record<string, () => string>>> = {
  questionnaire: () => portalText("operations", "merge.wizard.summary_questions"),
  interviewer_name: () => portalText("operations", "merge.wizard.summary_agent_name"),
  survey_subject: () => portalText("operations", "merge.wizard.summary_topic"),
  survey_commissioner: () => portalText("operations", "merge.wizard.summary_who_is_calling"),
};
const SURVEY_SUMMARY_FOLDED = new Set(["survey_arm_count", "questionnaire_arm2"]);

function summaryRowTermOf(field: AgentTemplateField): string {
  if (field.code === MENU_PREVIEW_FIELD_CODE && field.kind === "upload") return portalText("operations", "menu_57f5f5ef");
  return SURVEY_SUMMARY_TERM[field.code]?.() ?? summaryTermOf(field);
}

function summaryRowDetailOf(
  field: AgentTemplateField,
  fields: readonly AgentTemplateField[],
  context: WizardContext,
  upload: Readonly<Record<string, DocumentUploadProgress>>,
  menuItemCount: number | null,
  typedQuestion: Readonly<Record<string, number>>,
): string {
  const detail = fieldDetailOf(field, context, upload);
  if (field.code === MENU_PREVIEW_FIELD_CODE && field.kind === "upload" && menuItemCount !== null) {
    return `${detail} · ${portalText("operations", "merge.wizard.menu_items", { count: menuItemCount })}`;
  }
  if (field.code !== "questionnaire") return detail;
  const each = fields
    .filter((one) => SURVEY_QUESTIONNAIRE_FIELD.has(one.code))
    .map((one) => {
      const typed = typedQuestion[one.code];
      return typed === undefined ? fieldDetailOf(one, context, upload) : portalText("operations", "merge.wizard.typed_in_questions", { count: typed });
    })
    .join(", ");
  return context.answer.fieldValue["survey_arm_count"] === "2" ? portalText("operations", "merge.wizard.two_groups", { v0: each }) : each;
}

/** How many questions each typed questionnaire holds (a typed one is the `<code>.txt` the dropzone's box makes). */
function useTypedQuestionCount(file: Readonly<Record<string, File>>): Readonly<Record<string, number>> {
  const [count, setCount] = useState<Readonly<Record<string, number>>>({});
  useEffect(() => {
    let active = true;
    const typed = [...SURVEY_QUESTIONNAIRE_FIELD].flatMap((code) => {
      const one = file[code] as File | undefined;
      return one !== undefined && one.name === `${code}.txt` ? [[code, one] as const] : [];
    });
    void Promise.all(
      typed.map(async ([code, one]) => [code, (await one.text()).split("\n").filter((line) => line.trim() !== "").length] as const),
    ).then((pair) => {
      if (active) setCount(Object.fromEntries(pair));
    });
    return () => {
      active = false;
    };
  }, [file]);
  return count;
}

/** One field's answer, in words. */
function fieldDetailOf(field: AgentTemplateField, context: WizardContext, upload: Readonly<Record<string, DocumentUploadProgress>>): string {
  const { answer } = context;
  if (field.kind === "upload" && takesSeveralFiles(field)) {
    const named = namedFileOf(answer.fieldValue[field.code]).filter((name) => isSlotAttached(context, fileSlotOf(field, name)));
    return named.length === 0
      ? portalText("operations", "not_attached_4cb6b165")
      : named.map((name) => progressText(name, upload[fileSlotOf(field, name)])).join(", ");
  }
  if (field.kind === "upload") {
    const name = answer.fieldValue[field.code] ?? "";
    return name === "" ? portalText("operations", "not_attached_4cb6b165") : progressText(name, upload[field.code]);
  }
  if (field.kind === "website") {
    return websiteAddressSentOf(answer.fieldValue[field.code] ?? "") ?? portalText("operations", "not_set_93039e60");
  }
  if (field.kind === "choice") {
    const chosen = answer.fieldValue[field.code] ?? field.defaultValue;
    return field.options.find((option) => option.value === chosen)?.label ?? chosen;
  }
  if (field.kind === "text") {
    if (fieldFormatOf(field) === "opening_hours") return openingHoursLine(answer.openingWeek[field.code] ?? closedWeek());
    const value = (answer.fieldValue[field.code] ?? "").trim();
    return value === "" ? portalText("operations", "not_set_93039e60") : value;
  }
  return "";
}


function SummaryStep(props: SummaryStepProps): ReactNode {
  usePortalTranslation("operations");
  const { context, resolved, answering, row, steps, direction, upload, webChatOn, ceiling, shown, player, menuItemCount, onAnswer, onEdit } =
    props;
  const { card, answer } = context;
  const typedQuestion = useTypedQuestionCount(context.file);
  const duplicate = duplicateNameWarning(context, resolved);
  const outbound = direction === "outbound";
  const primary = resolved.languageCode[0];
  const greetingLanguage = primary === undefined ? null : languageNameOf(context.languagePack, primary);
  const greetingValue = answer.greetingEdited === true || answer.greeting !== "" ? answer.greeting : (resolved.greetingSuggestion ?? "");
  // plan-019 WAPP-FORM: the help assistant may propose the name while Review is on screen, through the same change as
  // typing (so the draft keeps it); `null` is "not typed yet" — the suggestion shows — and an undo goes back to it.
  const helpFill = useHelpFillable("new_agent_wizard", {
    display_name: {
      value: answer.displayName,
      set: (next) => {
        onAnswer((current) => ({ ...current, displayName: next }));
      },
      unset: () => {
        onAnswer((current) => ({ ...current, displayName: null }));
      },
    },
    // 2026-10-18: the greeting, as typed into the box below (the person's own words only, the help host checks).
    greeting: {
      value: greetingValue === "" ? null : greetingValue,
      set: (next) => {
        onAnswer((current) => ({ ...current, greeting: next, greetingEdited: true }));
      },
    },
  });

  const group: SummaryCard[] = [];
  for (const def of steps) {
    if (def.kind !== "content") continue;
    const fields = contentFieldOfPart(card, def.part, answer.fieldValue);
    const rows: SummaryRow[] = fields.flatMap((field) => [
      ...(SURVEY_SUMMARY_FOLDED.has(field.code)
        ? []
        : [{ term: summaryRowTermOf(field), detail: summaryRowDetailOf(field, fields, context, upload, menuItemCount, typedQuestion) }]),
      ...(fieldFormatOf(field) === "opening_hours" ? answeringSummaryRowOf(answering, answersAtAllHoursOf(card.code)) : []),
    ]);
    if (def.part === "orders" || (def.part === "all" && orderBookFieldOf(card) !== undefined)) {
      rows.unshift({ term: portalText("operations", "payment_b41a92be"), detail: paymentSummaryLineOf(answer.paymentMethod ?? DEFAULT_PAYMENT_METHOD) });
      rows.unshift({ term: portalText("operations", "orders_cded0933"), detail: portalText("operations", "wetalk_order_book_a2d90ad7") });
    }
    const title = def.part === "menu" ? portalText("operations", "menu_57f5f5ef") : def.part === "hours" ? portalText("operations", "opening_hours_6117e118") : def.part === "orders" ? portalText("operations", "orders_cded0933") : card.contentTitle;
    group.push({ title, step: def.step, row: rows });
  }
  const languageStep = steps.find((one) => one.kind === "language")?.step ?? 0;
  const mediumStep = steps.find((one) => one.kind === "medium")?.step ?? 0;
  // The export of 2026-10-10: the greeting language first, marked "(greets callers)", then the others.
  // plan-026 (0176): a beta language reads "Spanish (Beta)" on this line.
  const languageLine = row
    .map((one) => betaLanguageLabelOf(context.languagePack, one.languageCode, one.languageName))
    .map((name, index) => (index === 0 ? portalText("operations", "merge.wizard.v0_greets_callers.9ccb4a", { v0: String(name) }) : name))
    .join(", ");

  group.push({
    title: portalText("operations", "languages_and_voice_7c8b55f5"),
    step: languageStep,
    row: [
      // The export titles the row "Languages" however many there are.
      { term: portalText("operations", "languages_db07be18"), detail: languageLine === "" ? portalText("operations", "none_chosen_5c8fcbe2") : languageLine },
      ...row.map((one, index): SummaryRow => {
        const chosen = one.option.find((each) => each.voiceId === one.voiceId);
        return {
          term: portalText("operations", "value_voice_6ba228f8", { v0: String(one.languageName) }),
          detail: chosen?.name ?? portalText("operations", "no_voice_speaks_it_yet_521c2adc"),
          ...(chosen === undefined
            ? {}
            : { play: { voiceId: chosen.voiceId, voiceName: chosen.name, row: one, greeting: index === 0 ? resolved.greeting : null } }),
        };
      }),
    ],
  });
  group.push({
    title: portalText("operations", "channels_18e03e2a"),
    step: mediumStep,
    row: [
      { term: portalText("operations", "channels_18e03e2a"), detail: webChatOn ? portalText("operations", "calls_web_chat_3806003c") : portalText("operations", "calls_0a19b7e2") },
      {
        term: portalText("operations", "calls_at_once_cb8a5b2e"),
        detail: portalText("operations", "value_your_organization_allows_value_across_all_agents_fa34e965", { v0: String(String(resolved.channelUnitCount)), v1: String(String(ceiling)) }),

      },
      { term: portalText("operations", "recording_9b98e1a0"), detail: answer.recordingEnabled ? portalText("operations", "calls_are_recorded_callers_can_say_no_d50ecd79") : portalText("operations", "calls_are_not_recorded_2824e666") },
      { term: portalText("operations", "phone_77064d52"), detail: portalText("operations", "you_ll_connect_a_number_after_you_build_60b3a730") },
    ],
  });

  return (
    <>
      <Field
        label={portalText("operations", "agent_name_fc92420c")}
        {...(duplicate === null ? {} : { description: duplicate })}
        {...(shown[PROBLEM_KEY.displayName] === undefined ? {} : { error: shown[PROBLEM_KEY.displayName] })}
      >
        {(control) => (
          <Input
            {...control}
            name="displayName"
            maxLength={120}
            placeholder={resolved.suggestedName}
            value={resolved.displayName}
            {...helpFill.mark("display_name")}
            onChange={(event) => {
              const next = event.currentTarget.value;
              onAnswer((current) => ({ ...current, displayName: next }));
            }}
          />
        )}
      </Field>
      {/* The export of 2026-10-10 took the greeting box off the Languages step and draws it nowhere; it stays here,
          beside the name, because a language with no suggestion has none until it is written (no English fallback)
          and owner answer 11 plays the greeting as typed. The Languages card's first voice says it. */}
      <Field
        label={greetingLanguage === null ? portalText("operations", "greeting_9c983d4e") : portalText("operations", "greeting_in_value_58b42ea8", { v0: String(greetingLanguage) })}
        description={outbound ? portalText("operations", "the_person_hears_this_as_soon_as_they_pick_up_66fe7d8c") : portalText("operations", "callers_hear_this_as_soon_as_the_agent_picks_up_677a76fe")}
        {...(shown[PROBLEM_KEY.greeting] === undefined ? {} : { error: shown[PROBLEM_KEY.greeting] })}
      >
        {(control) => (
          <Textarea
            {...control}
            {...helpFill.mark("greeting")}
            name="greeting"
            rows={3}
            value={greetingValue}
            onChange={(event) => {
              // Read now: React nulls `currentTarget` once the handler returns.
              const next = event.currentTarget.value;
              onAnswer((current) => ({ ...current, greeting: next, greetingEdited: true }));
            }}
          />
        )}
      </Field>
      <div className="c-setup-review-cards">
        {group.map((one) => (
          <section key={one.title} className="c-setup-card c-setup-review-card" aria-label={one.title}>
            <div className="c-setup-review-head">
              <h2 className="c-setup-review-title">{one.title}</h2>
              <button
                type="button"
                className="btn btn-ghost c-setup-small-ghost"
                aria-label={portalText("operations", "edit_value_ad328111", { v0: String(one.title.toLowerCase()) })}
                onClick={() => {
                  onEdit(one.step);
                }}
              >
                {portalText("operations", "edit_5301648d")}</button>
            </div>
            <dl className="c-setup-review">
              {one.row.map((each) => (
                <div key={each.term} className="c-setup-review-row">
                  <dt className="c-setup-review-term">{each.term}</dt>
                  <dd className="c-setup-review-detail">
                    <span className="c-setup-review-value">{each.detail}</span>
                    {each.play === undefined ? null : (
                      <LanguageVoicePlayButton
                        player={player}
                        voiceId={each.play.voiceId}
                        voiceName={each.play.voiceName}
                        languageCode={each.play.row.languageCode}
                        languageName={each.play.row.languageName}
                        compact
                        greeting={each.play.greeting}
                      />
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
      {/* plan-017 WAPP-B (design §29.11 row 11): the organization's rates of the kinds this template implies. */}
      <SetupCost outbound={outbound} hasOrderBook={orderBookFieldOf(card) !== undefined} />
    </>
  );
}

// ── plan-015 AGC-UI: out of hours and every line busy ─────────────────────────────────────────

const AFTER_HOURS_WORD = {
  get closed() { return portalText("operations", "say_you_are_closed_f2b3c381"); },
  get message() { return portalText("operations", "take_a_message_434aed00"); },
  get forward() { return portalText("operations", "forward_to_a_phone_22b04382"); },
} as const;
const BUSY_WORD = {
  get queue() { return portalText("operations", "hold_them_in_a_queue_0837192f"); },
  get message() { return portalText("operations", "take_a_message_instead_cf24e6de"); },
  get forward() { return portalText("operations", "forward_to_a_phone_22b04382"); },
} as const;

/** The Review step's rows under "Opening hours": the special dates (the prototype's `specialLine`), the zone, the choices. */
function answeringSummaryRowOf(answering: WizardAnswering, answersAtAllHours: boolean): readonly SummaryRow[] {
  const { choice } = answering;
  const busy =
    choice.busyChoice === "queue"
      ? portalText("operations", "value_up_to_value_min_89ac36ee", { v0: String(BUSY_WORD.queue), v1: String(String(choice.longestWaitMinute ?? DEFAULT_WAIT_MINUTE)) })
      : BUSY_WORD[choice.busyChoice];
  return [
    { term: portalText("operations", "merge.wizard.holidays_and_special_days.bad2a8"), detail: specialDateLine(answering.specialDate) },
    ...(answering.timeZone === null ? [] : [{ term: portalText("operations", "time_zone_eea79afd"), detail: timeZoneLabelOf(answering.timeZone) }]),
    // 2026-10-14: an agent that answers at every hour has no out-of-hours choice to show.
    ...(answersAtAllHours ? [] : [{ term: portalText("operations", "out_of_hours_44882236"), detail: AFTER_HOURS_WORD[choice.afterHoursChoice] }]),
    { term: portalText("operations", "when_every_line_is_busy_ff7e5ac2"), detail: busy },

  ];
}

interface WizardAnsweringChoicesProps {
  readonly choice: AnsweringChoice;
  /** 2026-10-14: the engine answers at every hour — the "Out of hours" card is not drawn, a sentence says why. */
  readonly answersAtAllHours: boolean;
  readonly forwardFact: ForwardFact | null;
  readonly error: string | undefined;
  readonly onChoice: (choice: AnsweringChoice) => void;
}

/**
 * "Out of hours" and "When every line is busy" — the agent page's two cards (`design/WeTalk.dc.html` 4272–4304), asked
 * here so a new agent answers as the person decided from its first call.
 */
function WizardAnsweringChoices({ choice, answersAtAllHours, forwardFact, error, onChoice }: WizardAnsweringChoicesProps): ReactNode {
  usePortalTranslation("operations");
  const closedReason = forwardClosedReasonOf(forwardFact);
  const put = (next: Partial<AnsweringChoice>): void => {
    onChoice({ ...choice, ...next });
  };
  return (
    <>
      {answersAtAllHours ? <p className="c-answer-hours-dek">{portalText("operations", "answering.all_hours_dek")}</p> : null}
      <div className="c-answer-pair">
        {answersAtAllHours ? null : (
          <section className="c-answer-card" aria-label={portalText("operations", "out_of_hours_44882236")}>
            <h2 className="c-answer-card-heading">{portalText("operations", "out_of_hours_44882236")}</h2>
            <AnsweringChoiceGroup
              name="after-hours"
              label={portalText("operations", "out_of_hours_44882236")}
              option={afterHoursOptionOf(choice.forwardTo, forwardFact)}
              chosen={choice.afterHoursChoice}
              forwardClosedReason={closedReason}
              disabled={false}
              onChoose={(afterHoursChoice) => {
                put({ afterHoursChoice });
              }}
            />
          </section>
        )}
        <section className="c-answer-card" aria-label={portalText("operations", "when_every_line_is_busy_ff7e5ac2")}>
          <h2 className="c-answer-card-heading">{portalText("operations", "when_every_line_is_busy_ff7e5ac2")}</h2>
          <AnsweringChoiceGroup
            name="busy"
            label={portalText("operations", "when_every_line_is_busy_ff7e5ac2")}
            option={busyOptionOf(choice.forwardTo, forwardFact)}
            chosen={choice.busyChoice}
            forwardClosedReason={closedReason}
            disabled={false}
            onChoose={(busyChoice) => {
              put({
                busyChoice,
                longestWaitMinute:
                  busyChoice === "queue" ? (choice.longestWaitMinute ?? DEFAULT_WAIT_MINUTE) : choice.longestWaitMinute,
              });
            }}
          />
          {choice.busyChoice === "queue" ? (
            <LongestWaitField
              name="longest-wait"
              minute={choice.longestWaitMinute ?? DEFAULT_WAIT_MINUTE}
              note={waitNoteOf((choice.longestWaitMinute ?? DEFAULT_WAIT_MINUTE) * 60)}
              disabled={false}
              onMinute={(longestWaitMinute) => {
                put({ longestWaitMinute });
              }}
            />
          ) : null}
        </section>
      </div>
      {forwards(choice) && closedReason === null ? (
        <ForwardNumberField
          id="forward-to"
          value={choice.forwardTo ?? ""}
          problem={null}
          limitSentence={forwardLimitSentenceOf(forwardFact)}
          disabled={false}
          onValue={(forwardTo) => {
            put({ forwardTo });
          }}
        />
      ) : null}
      {error === undefined ? null : (
        <span className="wt-field-error" role="alert">
          {error}
        </span>
      )}
    </>
  );
}
